import { statusClass, statusLabel } from "@/lib/status";
import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import {
  ArrowLeftRight,
  ChevronDown,
  History,
  Ban,
  BedDouble,
  CalendarDays,
  CheckCircle2,
  CircleDollarSign,
  CircleUserRound,
  Clock3,
  LogIn,
  LogOut,
  Pencil,
  Percent,
  Plus,
  ReceiptText,
  Repeat,
  Undo2,
  Users,
  Utensils,
  type LucideIcon,
} from "lucide-react";
import { ApiError, api } from "@/lib/api-client";
import { queryClient } from "@/lib/query-client";
import { useAppStore } from "@/store/app-store";
import type { Booking, Payment, Room, StayChargeType } from "@/types/domain";
import type { InventoryRoom } from "@/features/inventory/types";
import { PROPERTY_TIME_ZONE, businessDateIso, currency } from "@/lib/format";
import { isAwaitingGuestPayment } from "@/lib/booking-window";
import { usePropertyAccess, type DiscountLimit } from "@/lib/property-access";
import { ActivityList, type ActivityEntry } from "@/features/activity/ActivityList";
import { Button } from "@/components/ui/Button";
import { DateInput } from "@/components/ui/DateInput";
import { RoomAllotmentPicker } from "@/features/bookings/RoomAllotmentPicker";
import { GuestRegister } from "@/features/bookings/GuestRegister";
import { InvoicePanel } from "@/features/bookings/InvoicePanel";
import { DepositPanel } from "@/features/bookings/DepositPanel";
import { allowedRoomTypes, needsText, useRoomSuggestion } from "@/features/bookings/useRoomSuggestion";
import { AddRoomForm, RoomStaysPanel, type RoomAction } from "@/features/bookings/RoomStaysPanel";
import { roomStayLabel } from "@/features/bookings/room-stay";
import { ResourceDialog, type ListResponse } from "@/components/resource/dialog-kit";
import { useBackCloseable } from "@/lib/modal-stack";
import { listItems } from "@/components/resource/dialog-utils";
import type { Service } from "@/features/settings/ServicesSettingsPage";
import type { RatePlan } from "@/features/settings/RatePlansSettingsPage";

type RemovedNightsPolicy = { type: "REFUND" | "CHARGE" | "MANUAL"; amount?: number; reason?: string };
type DayUseType = "FULL" | "HALF_DAY" | "MANUAL";
// Same-day check-out adds what the day costs and what happens to the further booked nights.
type StayCharge = { type: StayChargeType; charge?: number; waiverReason?: string; dayUseType?: DayUseType; dayUseCharge?: number; removedNightsPolicy?: RemovedNightsPolicy };
// GET /bookings/:id/stay-charge-quote - the server decides whether today is the right day and what an early / late
// charge would be (check-in only on the check-in date, check-out only on the check-out date, business day).
type StayChargeQuote = {
  kind: "CHECK_IN" | "CHECK_OUT";
  allowed: boolean;
  blockedReason: string | null;
  applicable: boolean;
  scheduledAt?: string;
  graceMinutes: number;
  businessDate: string;
  suggestedType: StayChargeType;
  fullCharge: number;
  halfDayCharge: number;
  halfDayPercent: number;
  chargeableRooms: number;
  overdueNights: number;
  overdueAmount: number;
  // Check-in refused because adults' guest-register details are missing.
  guestDetailsMissing?: boolean;
  // Check-out on the day of arrival.
  sameDay?: boolean;
  sameDayRooms?: number;
  dayUseFull?: number;
  dayUseHalf?: number;
  sameDayBookedAmount?: number;
  remainingNights?: number;
  remainingAmount?: number;
  refundable?: boolean;
  defaultRemainingPolicy?: "REFUND" | "CHARGE";
};
type DateChangeQuote = {
  available: boolean;
  availabilityMessage: string | null;
  previousNights: number;
  newNights: number;
  nightlyRate: number;
  previousTotal: number;
  newTotal: number;
  removedNights: number;
  removedAmount: number;
  removedContext: "EARLY_DEPARTURE" | "NO_SHOW" | "DATE_CHANGE" | null;
  refundable: boolean;
  defaultPolicy: "REFUND" | "CHARGE";
  rooms?: { bookingRoomId: string; roomName: string; available: boolean; previousAmount: number; newAmount: number }[];
};
// Every change is confirmed a second time from a summary of what will happen. Risky ones ask more:
// typing the booking ref's last 4 characters (cancel / reject / no-show) or ticking an acknowledgement (refund).
type PendingConfirm = {
  title: string;
  lines: [string, string][];
  note?: string;
  confirmLabel: string;
  danger?: boolean;
  typeToConfirm?: string;
  acknowledge?: string;
  // Asks for a reason (at least 3 characters) and hands it to run.
  reasonLabel?: string;
  run: (reason?: string) => void;
};
type WithScope = { bookingRoomIds?: string[] };
type BookingAction = "add-room" | "change-dates" | "check-in" | "check-out" | "record-payment" | "record-refund" | "extend" | "change-room" | "rate-plan" | "exchange" | "confirm-room" | null;

export type InitialBookingAction = "change-dates" | "check-in" | "check-out" | "extend" | "change-room" | "exchange" | "remove-room" | "add-room" | "record-payment";

export function BookingDetailDialog({ booking, initialAction, initialRoomIds, onClose }: { booking: Booking; role: "ADMIN" | "OWNER"; initialAction?: InitialBookingAction; initialRoomIds?: string[]; onClose: () => void }) {
  const [current, setCurrent] = useState(booking);
  // `booking` is whatever the caller had cached (a list row can be stale - the query client doesn't
  // refetch on focus - or another session/inventory action can have moved it on since). Fetch the
  // live record on open so buttons like Check in never fire against a booking that has, say, already
  // been fully checked out elsewhere.
  const liveBooking = useQuery({
    queryKey: ["booking", booking.id],
    queryFn: () => api<Booking>(`/bookings/${booking.id}`),
    staleTime: 0,
    refetchOnMount: "always",
  });
  useEffect(() => {
    if (liveBooking.data) setCurrent(liveBooking.data);
  }, [liveBooking.data]);
  const [activeAction, setActiveAction] = useState<BookingAction>(null);
  // Destructive status changes (cancel / reject) wait for an explicit second confirmation.
  const [pendingConfirm, setPendingConfirm] = useState<PendingConfirm | null>(null);
  const settle = { onSettled: () => setPendingConfirm(null) };
  // Amount to put in the payment form when a check-out needs money collected first.
  const [paymentPrefill, setPaymentPrefill] = useState<number | null>(null);
  const [moreOpen, setMoreOpen] = useState(false);
  const moreRef = useRef<HTMLDivElement>(null);
  useBackCloseable(moreOpen, () => setMoreOpen(false));
  const bodyRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!activeAction && !pendingConfirm) return;
    const frame = requestAnimationFrame(() => bodyRef.current?.querySelector(".booking-inline-form")?.scrollIntoView({ block: "nearest", behavior: "smooth" }));
    return () => cancelAnimationFrame(frame);
  }, [activeAction, pendingConfirm]);
  useEffect(() => {
    if (!moreOpen) return;
    const close = (event: MouseEvent) => { if (moreRef.current && !moreRef.current.contains(event.target as Node)) setMoreOpen(false); };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [moreOpen]);
  const [editingGuests, setEditingGuests] = useState(false);
  const [openGuestForm, setOpenGuestForm] = useState(false);
  const [addingService, setAddingService] = useState(false);
  const [addingCharge, setAddingCharge] = useState(false);
  const [addingDiscount, setAddingDiscount] = useState(false);
  const [chargeQuote, setChargeQuote] = useState<StayChargeQuote | null>(null);
  const [datesPassed, setDatesPassed] = useState(false);
  // Which rooms the running action applies to: undefined = every eligible room (bulk), otherwise just these.
  const [selectedRoomIds, setSelectedRoomIds] = useState<string[]>([]);
  const [actionScope, setActionScope] = useState<string[] | undefined>(undefined);
  const propertyId = useAppStore((state) => state.activeProperty!.id);
  const nights = Math.max(1, Math.round((new Date(current.checkOutDate).getTime() - new Date(current.checkInDate).getTime()) / 86400000));
  const paidAmount = (current.payments ?? []).filter((payment) => payment.status === "SUCCESS").reduce((sum, payment) => sum + Number(payment.amount), 0);
  const refundAmount = (current.payments ?? []).filter((payment) => payment.status === "REFUNDED").reduce((sum, payment) => sum + Number(payment.amount), 0);
  const due = Number(current.totalAmount) - (paidAmount - refundAmount);
  // Confirm-before-payment: confirmed by the owner, rooms held until the guest pays by paymentDueAt.
  const awaitingGuestPayment = isAwaitingGuestPayment(current);
  const paymentDueTime = awaitingGuestPayment
    ? new Date(current.paymentDueAt!).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", timeZone: PROPERTY_TIME_ZONE })
    : "";
  const balance = Math.max(0, due);
  const roomBasePrice = Number(current.room?.basePrice ?? 0);
  const ratePlanNightly = current.ratePlan
    ? current.ratePlan.amountType === "FLAT"
      ? roomBasePrice + Number(current.ratePlan.amountValue)
      : roomBasePrice + roomBasePrice * (Number(current.ratePlan.amountValue) / 100)
    : roomBasePrice;
  const roomSubtotal = roomBasePrice * nights;
  const ratePlanAdjustment = (ratePlanNightly - roomBasePrice) * nights;
  const liveServices = (current.services ?? []).filter((line) => !line.reversedAt);
  const liveCharges = (current.charges ?? []).filter((charge) => !charge.reversedAt);
  const liveDiscounts = (current.discounts ?? []).filter((discount) => !discount.reversedAt);
  const servicesSubtotal = liveServices.reduce((sum, line) => sum + Number(line.price) * line.quantity, 0);
  const servicesTax = liveServices.reduce((sum, line) => sum + Number(line.taxAmount ?? 0), 0);
  const extraChargesTotal = liveCharges.reduce((sum, charge) => sum + Number(charge.amount), 0);
  const discountsTotal = liveDiscounts.reduce((sum, discount) => sum + Number(discount.amount), 0);
  const earlyCheckinCharge = Number(current.earlyCheckinCharge ?? 0);
  const lateCheckoutCharge = Number(current.lateCheckoutCharge ?? 0);
  // Room GST: added on top (counted in the total) or already inside the room price (shown only).
  const roomTax = Number(current.roomTaxAmount ?? 0);
  const roomTaxAdded = current.gstInclusive === false ? roomTax : 0;
  // Bookings with allotted rooms price each room on its own stay (rate plan already inside its amount).
  const pricedRooms = (current.allottedRooms ?? []).filter((room) => room.status !== "CANCELLED");
  const roomsAmount = pricedRooms.reduce((sum, room) => sum + Number(room.amount), 0);
  const computedSubtotal = (pricedRooms.length ? roomsAmount : roomSubtotal + ratePlanAdjustment) + servicesSubtotal + servicesTax + extraChargesTotal + earlyCheckinCharge + lateCheckoutCharge - discountsTotal + roomTaxAdded;
  const otherAdjustment = Number(current.totalAmount) - computedSubtotal;
  const stayRooms = current.allottedRooms ?? [];
  const liveRooms = stayRooms.filter((room) => room.status !== "CANCELLED");
  const multiRoom = liveRooms.length > 1;
  const anyToCheckIn = stayRooms.length ? stayRooms.some((room) => room.status === "CONFIRMED") : current.bookingStatus === "CONFIRMED";
  const anyToCheckOut = stayRooms.length ? stayRooms.some((room) => room.status === "CHECKED_IN") : current.bookingStatus === "CHECKED_IN";
  const scopeLabel = selectedRoomIds.length ? ` (${selectedRoomIds.length} selected)` : multiRoom ? " (all rooms)" : "";
  const statusOptions = getBookingStatusOptions(current.bookingStatus);
  const canOperate = current.bookingStatus === "CONFIRMED" || current.bookingStatus === "CHECKED_IN";
  const isClosed = ["CANCELLED", "REJECTED", "EXPIRED", "AUTO_CANCELLED", "NO_SHOW"].includes(current.bookingStatus);
  const netPaid = paidAmount - refundAmount;
  // Services / charges / guests only while the stay is live (not pending, not waiting for the app payment).
  // What this person may do here (main owner / member permissions); the server checks every action too.
  const access = usePropertyAccess();
  const canEditExtras = canOperate && !awaitingGuestPayment;
  const canChargesServices = canEditExtras && access.can("CHARGES_SERVICES");
  const canDiscounts = canEditExtras && access.can("DISCOUNTS");
  const canEditGuests = canEditExtras && access.can("BOOKINGS_MANAGE");
  // App (GoAdapter) bookings: dates, extend and early/late check-in can change at the hotel - the
  // difference is collected or refunded here and reported back to the app. Rooms and rate plan can't (N-2, N-4).
  const prepaidApp = current.source === "SAAS_ADAPTER";
  // A day-use booking is one day's hours: its dates, rooms and rate plan don't change (cancel and rebook instead).
  const dayUse = Boolean(current.isDayUse);
  const canChangeDates = canOperate && !awaitingGuestPayment && !dayUse && access.can("STAY_CHANGE");
  const canMoveRooms = canOperate && !awaitingGuestPayment && !dayUse && access.can("STAY_CHANGE");
  // No-show: confirmed, nobody checked in, and the late-arrival cutoff after the check-in date has passed.
  const businessToday = businessDateIso(current.stayProfile?.lateArrivalCutoff ?? "06:00");
  const canMarkNoShow = access.can("CANCEL") && current.bookingStatus === "CONFIRMED" && !stayRooms.some((room) => room.status === "CHECKED_IN" || room.status === "CHECKED_OUT")
    && current.checkInDate.slice(0, 10) < businessToday;
  // Cancelled / no-show with money on it: suggest a refund from the rate plan and the property's cancellation fee.
  const refundable = current.ratePlan?.isRefundable ?? true;
  const cancellationFeePercent = current.ratePlan?.freeCancellation ? 0 : Number(current.stayProfile?.cancellationFeePercent ?? 0);
  const suggestedRefund = !isClosed || netPaid <= 0 ? 0
    : current.bookingStatus === "NO_SHOW" || !refundable ? 0
      : Math.max(0, Math.round((netPaid - (Number(current.totalAmount) * cancellationFeePercent) / 100) * 100) / 100);
  const ref4 = current.bookingRef.slice(-4).toUpperCase();
  const roomsLabel = (ids?: string[]) => {
    const pool = stayRooms.filter((room) => room.status !== "CANCELLED" && (!ids?.length || ids.includes(room.id)));
    return pool.length ? pool.map((room) => room.room.name).join(", ") : current.room?.name ?? current.roomType?.name ?? "Room";
  };

  const [initialActionHandled, setInitialActionHandled] = useState(false);
  const applyUpdate = (updated: Booking) => {
    setCurrent(updated);
    setActiveAction(null);
    setSelectedRoomIds([]);
    setActionScope(undefined);
    void queryClient.invalidateQueries({ queryKey: ["bookings"] });
  };

  const statusMutation = useMutation({
    mutationFn: (input: { status: Booking["bookingStatus"]; roomIds?: string[] }) => api<Booking>(`/bookings/${current.id}/status`, { method: "PATCH", body: input }),
    onSuccess: applyUpdate,
  });
  const checkInMutation = useMutation({
    mutationFn: (input: StayCharge & WithScope) => api<Booking>(`/bookings/${current.id}/check-in`, { method: "PATCH", body: input }),
    onSuccess: applyUpdate,
  });
  const checkOutMutation = useMutation({
    mutationFn: (input: StayCharge & WithScope) => api<Booking>(`/bookings/${current.id}/check-out`, { method: "PATCH", body: input }),
    onSuccess: applyUpdate,
    // The server refuses the final check-out until the balance is settled - take the user straight to the form that settles it.
    onError: (error) => {
      if (!(error instanceof ApiError) || error.status !== 422) return;
      if (/overpaid/i.test(error.message)) setActiveAction("record-refund");
      else if (/payment of .* is pending/i.test(error.message)) setActiveAction("record-payment");
    },
  });
  // A 409 here almost always means `current` was stale (booking moved on elsewhere) - refetch so the
  // buttons correct themselves instead of the user retrying against a booking state that no longer exists.
  useEffect(() => {
    if (checkInMutation.error || checkOutMutation.error) void liveBooking.refetch().then((result) => { if (result.data) setCurrent(result.data); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkInMutation.error, checkOutMutation.error]);
  const changeDatesMutation = useMutation({
    mutationFn: (input: { checkInDate: string; checkOutDate: string; removedNightsPolicy?: RemovedNightsPolicy } & WithScope) => api<Booking>(`/bookings/${current.id}/dates`, { method: "PATCH", body: input }),
    onSuccess: applyUpdate,
  });
  const noShowMutation = useMutation({
    mutationFn: () => api<Booking>(`/bookings/${current.id}/no-show`, { method: "PATCH" }),
    onSuccess: applyUpdate,
  });
  const [quoting, setQuoting] = useState(false);
  const startStayAction = async (kind: "check-in" | "check-out", ids?: string[]) => {
    setActionScope(ids);
    setQuoting(true);
    try {
      const scope = ids?.length ? `&bookingRoomIds=${ids.join(",")}` : "";
      const quote = await api<StayChargeQuote>(`/bookings/${current.id}/stay-charge-quote?kind=${kind}${scope}`);
      setChargeQuote(quote);
      if (!quote.allowed || quote.applicable || quote.overdueNights > 0 || quote.sameDay) setActiveAction(kind);
      else confirmStay(kind, { type: "NONE" }, quote, ids);
    } catch (error) {
      setChargeQuote(null);
      setActiveAction(kind);
      console.error(error);
    } finally {
      setQuoting(false);
    }
  };
  const chargeAmount = (input: StayCharge, quote: StayChargeQuote | null) =>
    !quote?.applicable ? 0
      : input.type === "FULL" ? quote.fullCharge
        : input.type === "HALF_DAY" ? quote.halfDayCharge
          : input.type === "MANUAL" ? (input.charge ?? 0) * Math.max(1, quote.chargeableRooms)
            : 0;
  // Summary before a check-in / check-out. A check-out that finishes the booking needs the money settled first,
  // so if the extras leave a balance the confirmation offers to collect (or refund) it instead.
  const confirmStay = (kind: "check-in" | "check-out", input: StayCharge, quote: StayChargeQuote | null, ids?: string[]) => {
    const charge = chargeAmount(input, quote);
    const overdue = kind === "check-out" ? quote?.overdueAmount ?? 0 : 0;
    const sameDay = kind === "check-out" && Boolean(quote?.sameDay);
    const dayUse = !sameDay ? 0
      : input.dayUseType === "HALF_DAY" ? quote!.dayUseHalf ?? 0
        : input.dayUseType === "MANUAL" ? (input.dayUseCharge ?? 0) * Math.max(1, quote!.sameDayRooms ?? 1)
          : quote!.dayUseFull ?? 0;
    const remainingAmount = quote?.remainingAmount ?? 0;
    const retained = !sameDay || !quote?.remainingNights ? 0
      : input.removedNightsPolicy?.type === "CHARGE" ? remainingAmount
        : input.removedNightsPolicy?.type === "MANUAL" ? input.removedNightsPolicy.amount ?? 0
          : input.removedNightsPolicy?.type === "REFUND" ? 0
            : quote.defaultRemainingPolicy === "CHARGE" ? remainingAmount : 0;
    const sameDayDelta = sameDay ? dayUse - (quote!.sameDayBookedAmount ?? 0) + retained : 0;
    const extras = Math.round((charge + overdue + sameDayDelta) * 100) / 100;
    const scopeIds = ids?.length ? ids : stayRooms.filter((room) => room.status === (kind === "check-in" ? "CONFIRMED" : "CHECKED_IN")).map((room) => room.id);
    const finishing = kind === "check-out" && !stayRooms.some((room) => (room.status === "CONFIRMED" || room.status === "CHECKED_IN") && !scopeIds.includes(room.id));
    const balanceAfter = Math.round((due + extras) * 100) / 100;
    const lines: [string, string][] = [
      ["Guest", current.guestName],
      ["Room(s)", roomsLabel(ids)],
      ...(overdue > 0 ? [[`Overdue night(s) (${quote?.overdueNights})`, currency.format(overdue)] as [string, string]] : []),
      ...(sameDay ? [["Today (same-day check-out)", dayUse > 0 ? currency.format(dayUse) : "No charge"] as [string, string]] : []),
      ...(sameDay && quote?.remainingNights ? [[`Remaining ${quote.remainingNights} night(s)`, retained > 0 ? `${currency.format(retained)} kept` : "Refunded"] as [string, string]] : []),
      ...(quote?.applicable ? [[kind === "check-in" ? "Early check-in charge" : "Late check-out charge", charge > 0 ? currency.format(charge) : "Waived"] as [string, string]] : []),
      ...(input.waiverReason ? [["Waiver reason", input.waiverReason] as [string, string]] : []),
      ...(extras !== 0 ? [["New total", currency.format(Number(current.totalAmount) + extras)] as [string, string]] : []),
      ["Balance after", balanceAfter === 0 ? "Settled" : balanceAfter > 0 ? `${currency.format(balanceAfter)} due` : `${currency.format(-balanceAfter)} to refund`],
    ];
    if (finishing && balanceAfter > 0.005) {
      setPendingConfirm({
        title: "Collect payment before check-out", lines, confirmLabel: `Record payment ${currency.format(balanceAfter)}`,
        note: "The final check-out needs the balance settled. Record the payment, then check out again.",
        run: () => { setPaymentPrefill(balanceAfter); setActiveAction("record-payment"); setPendingConfirm(null); },
      });
      return;
    }
    if (finishing && balanceAfter < -0.005) {
      setPendingConfirm({
        title: "Refund before check-out", lines, confirmLabel: `Record refund ${currency.format(-balanceAfter)}`,
        note: "The guest has paid more than the total. Record the refund, then check out again.",
        run: () => { setActiveAction("record-refund"); setPendingConfirm(null); },
      });
      return;
    }
    const mutation = kind === "check-in" ? checkInMutation : checkOutMutation;
    setPendingConfirm({
      title: kind === "check-in" ? "Check in this guest?" : "Check out this guest?",
      lines,
      confirmLabel: kind === "check-in" ? "Yes, check in" : "Yes, check out",
      run: () => mutation.mutate({ ...input, bookingRoomIds: ids }, settle),
    });
  };
  // One entry point for every room action: `ids` = a specific room (its own card), or the ticked rooms, or all eligible.
  const runRoomAction = (action: RoomAction | "add-room", explicit?: string[]) => {
    const ids = explicit ?? (selectedRoomIds.length ? selectedRoomIds : undefined);
    setActionScope(ids);
    if (action === "check-in" || action === "check-out") return void startStayAction(action, ids);
    if (action === "remove-room") {
      const stay = stayRooms.find((room) => room.id === ids?.[0]);
      if (stay) {
        setPendingConfirm({
          title: "Remove this room from the booking?", danger: true, confirmLabel: "Yes, remove room",
          lines: [["Room", roomStayLabel(stay)], ["Total goes down by", currency.format(Number(stay.amount) + Number(stay.earlyCheckinCharge ?? 0) + Number(stay.lateCheckoutCharge ?? 0))]],
          run: () => removeRoomMutation.mutate(stay.id, settle),
        });
      }
      return;
    }
    setDatesPassed(false);
    setActiveAction(action);
  };
  useEffect(() => {
    if (!initialAction || initialActionHandled) return;
    setInitialActionHandled(true);
    if (initialAction === "record-payment") setActiveAction("record-payment");
    else runRoomAction(initialAction, initialRoomIds);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialAction, initialActionHandled]);
  const addRoomMutation = useMutation({
    mutationFn: (roomId: string) => api<Booking>(`/bookings/${current.id}/rooms`, { method: "POST", body: { roomId } }),
    onSuccess: applyUpdate,
  });
  const removeRoomMutation = useMutation({
    mutationFn: (bookingRoomId: string) => api<Booking>(`/bookings/${current.id}/rooms/${bookingRoomId}`, { method: "DELETE" }),
    onSuccess: applyUpdate,
  });
  const recordPaymentMutation = useMutation({
    mutationFn: async (input: { amount: number; paymentMode: "CASH" | "UPI" | "CARD" | "BANK_TRANSFER" | "OTHER"; transactionRef?: string; allowDuplicateRef?: boolean }) => {
      await api<Payment>(`/payments`, { method: "POST", body: { bookingId: current.id, ...input } });
      return api<Booking>(`/bookings/${current.id}`);
    },
    onSuccess: applyUpdate,
  });
  const recordRefundMutation = useMutation({
    mutationFn: async (input: { amount: number; paymentMode: "CASH" | "UPI" | "CARD" | "BANK_TRANSFER" | "OTHER"; transactionRef?: string; allowDuplicateRef?: boolean }) => {
      await api<Payment>(`/payments`, { method: "POST", body: { bookingId: current.id, status: "REFUNDED", ...input } });
      return api<Booking>(`/bookings/${current.id}`);
    },
    onSuccess: applyUpdate,
  });
  const extendMutation = useMutation({
    mutationFn: (input: { checkOutDate: string } & WithScope) => api<Booking>(`/bookings/${current.id}/extend`, { method: "PATCH", body: input }),
    onSuccess: applyUpdate,
  });
  const roomChangeMutation = useMutation({
    mutationFn: (input: RoomMoveInput & { bookingRoomId?: string }) => api<Booking>(`/bookings/${current.id}/room`, { method: "PATCH", body: input }),
    onSuccess: applyUpdate,
  });
  const ratePlanMutation = useMutation({
    mutationFn: (input: { ratePlanId: string | null }) => api<Booking>(`/bookings/${current.id}/rate-plan`, { method: "PATCH", body: input }),
    onSuccess: applyUpdate,
  });
  const exchangeMutation = useMutation({
    mutationFn: (input: { withBookingId: string; withBookingRoomId?: string; bookingRoomId?: string }) => api<Booking>(`/bookings/${current.id}/exchange`, { method: "POST", body: input }),
    onSuccess: applyUpdate,
  });
  const occupancyMutation = useMutation({
    mutationFn: (input: { adults: number; children: number; extraCharge?: number }) => api<Booking>(`/bookings/${current.id}/occupancy`, { method: "PATCH", body: input }),
    onSuccess: (updated) => { setCurrent(updated); setEditingGuests(false); void queryClient.invalidateQueries({ queryKey: ["bookings"] }); },
  });
  const addServiceMutation = useMutation({
    mutationFn: (input: { serviceId: string; quantity: number }) => api<Booking>(`/bookings/${current.id}/services`, { method: "POST", body: input }),
    onSuccess: (updated) => { setCurrent(updated); setAddingService(false); void queryClient.invalidateQueries({ queryKey: ["bookings"] }); },
  });
  const updateServiceQtyMutation = useMutation({
    mutationFn: (input: { lineId: string; quantity: number }) => api<Booking>(`/bookings/${current.id}/services/${input.lineId}`, { method: "PATCH", body: { quantity: input.quantity } }),
    onSuccess: (updated) => { setCurrent(updated); void queryClient.invalidateQueries({ queryKey: ["bookings"] }); },
  });
  const removeServiceMutation = useMutation({
    mutationFn: ({ lineId, reason }: { lineId: string; reason: string }) => api<Booking>(`/bookings/${current.id}/services/${lineId}/reverse`, { method: "POST", body: { reason } }),
    onSuccess: (updated) => { setCurrent(updated); void queryClient.invalidateQueries({ queryKey: ["bookings"] }); },
  });
  const addChargeMutation = useMutation({
    mutationFn: (input: { label: string; amount: number }) => api<Booking>(`/bookings/${current.id}/charges`, { method: "POST", body: input }),
    onSuccess: (updated) => { setCurrent(updated); setAddingCharge(false); void queryClient.invalidateQueries({ queryKey: ["bookings"] }); },
  });
  // Above the member's limit the server answers 202 { pendingApproval } - the main owner decides.
  const [discountNotice, setDiscountNotice] = useState<string | null>(null);
  const addDiscountMutation = useMutation({
    mutationFn: (input: { type: "AMOUNT" | "PERCENT"; value: number; reason: string }) => api<Booking | { pendingApproval: true; amount: number }>(`/bookings/${current.id}/discounts`, { method: "POST", body: input }),
    onSuccess: async (result) => {
      setAddingDiscount(false);
      if ("pendingApproval" in result) {
        setDiscountNotice(`${currency.format(result.amount)} is above your discount limit - sent to the main owner for approval.`);
        const fresh = await liveBooking.refetch();
        if (fresh.data) setCurrent(fresh.data);
      } else {
        setDiscountNotice(null);
        setCurrent(result);
      }
      void queryClient.invalidateQueries({ queryKey: ["bookings"] });
    },
  });
  const decideDiscountMutation = useMutation({
    mutationFn: ({ requestId, approve, note }: { requestId: string; approve: boolean; note?: string }) =>
      api<Booking>(`/bookings/${current.id}/discount-requests/${requestId}/${approve ? "approve" : "reject"}`, { method: "POST", body: approve ? {} : { note } }),
    onSuccess: (updated) => { setCurrent(updated); void queryClient.invalidateQueries({ queryKey: ["bookings"] }); },
  });
  const removeDiscountMutation = useMutation({
    mutationFn: ({ discountId, reason }: { discountId: string; reason: string }) => api<Booking>(`/bookings/${current.id}/discounts/${discountId}/reverse`, { method: "POST", body: { reason } }),
    onSuccess: (updated) => { setCurrent(updated); void queryClient.invalidateQueries({ queryKey: ["bookings"] }); },
  });
  const removeChargeMutation = useMutation({
    mutationFn: ({ chargeId, reason }: { chargeId: string; reason: string }) => api<Booking>(`/bookings/${current.id}/charges/${chargeId}/reverse`, { method: "POST", body: { reason } }),
    onSuccess: (updated) => { setCurrent(updated); void queryClient.invalidateQueries({ queryKey: ["bookings"] }); },
  });

  // One primary action per status, money action second, everything else behind "More actions".
  type ActionItem = { key: string; label: string; icon: LucideIcon; onSelect: () => void; disabled?: boolean; danger?: boolean };
  const stageActions: ActionItem[] = [];
  if (statusOptions.includes("CONFIRMED") && access.can("BOOKINGS_MANAGE")) stageActions.push({ key: "confirm", label: "Confirm booking", icon: CheckCircle2, disabled: statusMutation.isPending, onSelect: () => setActiveAction("confirm-room") });
  if (anyToCheckIn && access.can("CHECK_IN_OUT")) stageActions.push({ key: "check-in", label: `Check in${scopeLabel}`, icon: LogIn, disabled: quoting || checkInMutation.isPending, onSelect: () => runRoomAction("check-in") });
  if (anyToCheckOut && access.can("CHECK_IN_OUT")) stageActions.push({ key: "check-out", label: `Check out${scopeLabel}`, icon: LogOut, disabled: quoting || checkOutMutation.isPending, onSelect: () => runRoomAction("check-out") });
  // Payment while the stay is live or finished with money still owed; refund whenever the guest paid more than
  // the total, or on a cancelled / no-show booking that still holds money.
  const moneyAction: ActionItem | null = access.can("PAYMENTS") && !isClosed && !awaitingGuestPayment && current.bookingStatus !== "PENDING" && due > 0
    ? { key: "payment", label: `Record payment (${currency.format(due)})`, icon: CircleDollarSign, onSelect: () => { setPaymentPrefill(null); setActiveAction("record-payment"); } }
    : access.can("REFUNDS") && ((!isClosed && due < 0) || (isClosed && netPaid > 0))
    ? { key: "refund", label: "Record refund", icon: CircleDollarSign, onSelect: () => setActiveAction("record-refund") }
    : null;
  const mainAction = stageActions[0] ?? moneyAction;
  const secondaryAction = stageActions[0] ? moneyAction : null;
  const moreActions: ActionItem[] = [
    ...stageActions.slice(1),
    ...(canChangeDates ? [
      { key: "change-dates", label: `Change stay dates${scopeLabel}`, icon: CalendarDays, onSelect: () => runRoomAction("change-dates") },
      { key: "extend", label: `Extend stay${scopeLabel}`, icon: Clock3, onSelect: () => runRoomAction("extend") },
    ] : []),
    ...(canMoveRooms && !multiRoom ? [
      { key: "change-room", label: "Change room", icon: Repeat, onSelect: () => runRoomAction("change-room", liveRooms[0] ? [liveRooms[0].id] : undefined) },
      { key: "exchange", label: "Exchange room", icon: ArrowLeftRight, onSelect: () => runRoomAction("exchange", liveRooms[0] ? [liveRooms[0].id] : undefined) },
    ] : []),
    ...(canMoveRooms && !prepaidApp ? [{ key: "add-room", label: "Add room", icon: Plus, onSelect: () => runRoomAction("add-room") }] : []),
    ...(canEditExtras && access.can("STAY_CHANGE") && !prepaidApp && !dayUse ? [{ key: "rate-plan", label: "Rate plan", icon: Percent, onSelect: () => setActiveAction("rate-plan") }] : []),
    ...(canMarkNoShow ? [{
      key: "no-show", label: "Mark no-show", icon: Ban, danger: true, disabled: noShowMutation.isPending,
      onSelect: () => setPendingConfirm({
        title: "Mark this booking as no-show?", danger: true, typeToConfirm: ref4, confirmLabel: "Yes, mark no-show",
        lines: [["Guest", current.guestName], ["Check-in date", formatDateTime(current.checkInDate, current.stayProfile?.checkInTime)], ["Room(s)", roomsLabel()], ["Paid so far", currency.format(netPaid)]],
        note: "The rooms are released. Payments stay on the booking - record a refund separately if your policy allows one.",
        run: () => noShowMutation.mutate(undefined, settle),
      }),
    }] : []),
    ...statusOptions.filter((status) => status !== "CONFIRMED" && access.can(status === "CANCELLED" ? "CANCEL" : "BOOKINGS_MANAGE")).map((status) => ({
      key: status, label: `${statusActionLabel(status)} booking`, icon: Ban, danger: true, disabled: statusMutation.isPending,
      onSelect: () => setPendingConfirm({
        title: `${statusActionLabel(status)} this booking?`, danger: true, typeToConfirm: ref4, confirmLabel: `Yes, ${statusActionLabel(status).toLowerCase()}`,
        lines: [["Guest", current.guestName], ["Stay", `${formatDateTime(current.checkInDate)} → ${formatDateTime(current.checkOutDate)}`], ["Paid so far", currency.format(netPaid)]],
        note: `${current.guestName} will be notified and this can't be undone.${prepaidApp && netPaid > 0 ? " The app refunds the guest's app payment automatically." : netPaid > 0 ? " Record any refund separately afterwards." : ""}`,
        run: () => statusMutation.mutate({ status }, settle),
      }),
    })),
  ];
  const roomsText = (current.roomsCount ?? 1) > 1 && current.allottedRooms?.length
    ? current.allottedRooms.map((entry) => entry.room.name).join(", ")
    : current.room?.name ?? (current.roomType ? `${current.roomType.name} · not yet allotted` : "Room");
  const sourceText = (current.source ?? "DIRECT_SITE").replaceAll("_", " ").toLowerCase();

  return (
    <ResourceDialog title={current.bookingRef} eyebrow={`Booking · ${sourceText}`} dialogClassName="booking-dialog" onClose={onClose}>
      <div className="booking-detail" ref={bodyRef}>
        <section className="bd-head">
          <h3 className="bd-guest">{current.guestName}</h3>
          <div className="bd-badges">
            <span className={statusClass(current.bookingStatus)}>{statusLabel(current.bookingStatus)}</span>
            <span className={statusClass(current.paymentStatus ?? "UNPAID")}>{statusLabel(current.paymentStatus ?? "UNPAID")}</span>
          </div>
        </section>

        <section className="bd-summary" aria-label="Stay summary">
          <div className="bd-summary__dates">
            <div><small>Check-in</small><strong className="num">{formatDateTime(current.checkInDate, current.stayProfile?.checkInTime)}</strong></div>
            <span className="bd-nights">{dayUse ? `Day-use ${current.dayUseFrom ?? ""}–${current.dayUseTo ?? ""}` : `${nights} night${nights === 1 ? "" : "s"}`}</span>
            <div className="bd-summary__end"><small>Check-out</small><strong className="num">{formatDateTime(current.checkOutDate, current.stayProfile?.checkOutTime)}</strong></div>
          </div>
          <div className="bd-summary__facts">
            <div><small>Room{(current.roomsCount ?? 1) > 1 ? `s (${current.roomsCount})` : ""}</small><strong>{roomsText}</strong></div>
            <div><small>Guests</small><strong>{guestCountText(current)}</strong></div>
            <div><small>Total</small><strong className="num">{currency.format(Number(current.totalAmount))}</strong></div>
          </div>
          {awaitingGuestPayment ? (
            <div className="bd-balance bd-balance--info"><div><strong>Awaiting guest payment</strong><small>Until {paymentDueTime} · auto-cancels if unpaid</small></div></div>
          ) : due !== 0 ? (
            <div className={due < 0 ? "bd-balance bd-balance--refund" : "bd-balance"}>
              <div><strong>{due < 0 ? "Refund due" : "Balance due"}</strong><small className="num">{currency.format(paidAmount - refundAmount)} paid of {currency.format(Number(current.totalAmount))}</small></div>
              <b className="num">{currency.format(Math.abs(due))}</b>
            </div>
          ) : (
            <div className="bd-balance bd-balance--settled"><div><strong>Fully paid</strong><small className="num">{currency.format(paidAmount - refundAmount)} received</small></div><CheckCircle2 size={22} /></div>
          )}
        </section>

        {stayRooms.length > 1 && (
          <RoomStaysPanel
            booking={current}
            canOperate={canOperate && access.can("CHECK_IN_OUT")}
            canChangeRooms={canMoveRooms}
            prepaidApp={prepaidApp}
            canChangeDates={canChangeDates}
            selected={selectedRoomIds}
            busy={quoting || checkInMutation.isPending || checkOutMutation.isPending || removeRoomMutation.isPending}
            onSelect={setSelectedRoomIds}
            onAction={runRoomAction}
          />
        )}
        {removeRoomMutation.error && <div className="form-error">{removeRoomMutation.error.message}</div>}

        {statusMutation.error && <div className="form-error">{statusMutation.error.message}</div>}
        {noShowMutation.error && <div className="form-error">{noShowMutation.error.message}</div>}
        {isClosed && netPaid > 0 && (
          <div className="form-warning">
            {currency.format(netPaid)} is still held on this {statusLabel(current.bookingStatus).toLowerCase()} booking.{" "}
            {suggestedRefund > 0 ? `Suggested refund: ${currency.format(suggestedRefund)}${cancellationFeePercent ? ` (after the ${cancellationFeePercent}% cancellation fee)` : ""}.` : current.bookingStatus === "NO_SHOW" ? "No-show: nothing is refunded unless you decide to." : "Non-refundable rate plan: nothing is refunded unless you decide to."}
          </div>
        )}
        {prepaidApp && canEditExtras && <div className="form-warning">App booking: stay dates, early check-in, late check-out and guests can be changed here - any difference is collected or refunded at the hotel and shown to the guest in the app. Rooms and rate plan can't be changed.</div>}
        {(checkInMutation.error || checkOutMutation.error) && !activeAction && <div className="form-error">{(checkInMutation.error ?? checkOutMutation.error)?.message}</div>}
        {activeAction === "change-dates" && (
          <ChangeStayDatesForm
            booking={current}
            notice={datesPassed ? chargeQuote?.blockedReason ?? "Pick new stay dates to continue." : undefined}
            loading={changeDatesMutation.isPending}
            error={changeDatesMutation.error}
            onCancel={() => setActiveAction(null)}
            scope={actionScope}
            onSubmit={(input, lines) => setPendingConfirm({
              title: "Change the stay dates?", lines: [["Guest", current.guestName], ["Room(s)", roomsLabel(actionScope)], ...lines], confirmLabel: "Yes, change dates",
              run: () => changeDatesMutation.mutate({ ...input, bookingRoomIds: actionScope }, settle),
            })}
          />
        )}

        {activeAction === "check-in" && (
          <StayChargeForm
            title="Check in guest"
            chargeLabel="Early check-in charge"
            quote={chargeQuote}
            loading={checkInMutation.isPending}
            error={checkInMutation.error}
            banner={chargeQuote?.applicable ? `Early check-in - check-in time is ${formatDateTime(current.checkInDate, current.stayProfile?.checkInTime)} (grace ${chargeQuote.graceMinutes} min). Choose the charge, or waive it with a reason.` : undefined}
            onChangeDates={chargeQuote?.guestDetailsMissing ? undefined : canChangeDates ? () => { setDatesPassed(true); setActiveAction("change-dates"); } : undefined}
            onFillGuests={chargeQuote?.guestDetailsMissing ? () => { setActiveAction(null); setOpenGuestForm(true); } : undefined}
            onCancel={() => setActiveAction(null)}
            onSubmit={(input) => confirmStay("check-in", input, chargeQuote, actionScope)}
          />
        )}
        {activeAction === "record-payment" && (
          <RecordPaymentForm
            balance={paymentPrefill ?? balance}
            loading={recordPaymentMutation.isPending}
            error={recordPaymentMutation.error}
            onCancel={() => { setActiveAction(null); setPaymentPrefill(null); }}
            onSubmit={(input) => setPendingConfirm({
              title: "Record this payment?",
              lines: [
                ["Booking", `${current.bookingRef} - ${current.guestName}`],
                ["Amount", currency.format(input.amount)],
                ["Mode", input.paymentMode.replace("_", " ").toLowerCase()],
                ["Reference", input.transactionRef ?? "None"],
                ["Balance after", currency.format(Math.max(0, due - input.amount))],
              ],
              note: input.allowDuplicateRef ? "This transaction reference is already recorded elsewhere - you chose to record it anyway." : undefined,
              confirmLabel: "Yes, record payment",
              run: () => recordPaymentMutation.mutate(input, { onSettled: () => setPendingConfirm(null), onSuccess: () => setPaymentPrefill(null) }),
            })}
          />
        )}
        {activeAction === "record-refund" && (
          <RecordRefundForm
            maxRefund={isClosed ? Math.max(0, netPaid) : Math.abs(Math.min(0, due))}
            suggested={isClosed ? suggestedRefund : Math.abs(Math.min(0, due))}
            message={isClosed
              ? `${currency.format(netPaid)} is held on this ${statusLabel(current.bookingStatus).toLowerCase()} booking. Suggested refund ${currency.format(suggestedRefund)} - change it if your policy says otherwise.`
              : `Guest has been overpaid by ${currency.format(Math.abs(Math.min(0, due)))}. Record a refund before checking out.`}
            loading={recordRefundMutation.isPending}
            error={recordRefundMutation.error}
            onCancel={() => setActiveAction(null)}
            onSubmit={(input) => setPendingConfirm({
              title: "Record this refund?",
              lines: [
                ["Booking", `${current.bookingRef} - ${current.guestName}`],
                ["Refund amount", currency.format(input.amount)],
                ["Refund mode", input.paymentMode.replace("_", " ").toLowerCase()],
                ["Reference", input.transactionRef ?? "None"],
                ["Paid after refund", currency.format(netPaid - input.amount)],
              ],
              acknowledge: `I have returned ${currency.format(input.amount)} to the guest`,
              confirmLabel: "Yes, record refund",
              danger: true,
              run: () => recordRefundMutation.mutate(input, settle),
            })}
          />
        )}
        {activeAction === "check-out" && (
          <StayChargeForm
            title="Check out guest"
            chargeLabel="Late check-out charge"
            quote={chargeQuote}
            loading={checkOutMutation.isPending}
            error={checkOutMutation.error}
            banner={[
              chargeQuote?.sameDay ? "The guest is leaving on the day of arrival. Choose what today costs; the room is free again tonight." : "",
              chargeQuote?.overdueNights ? `The check-out date has passed - ${chargeQuote.overdueNights} overdue night(s) (${currency.format(chargeQuote.overdueAmount)}) will be added and the stay ends today.` : "",
              chargeQuote?.applicable ? `Late check-out - check-out time was ${chargeQuote.scheduledAt ? formatDateTime(chargeQuote.scheduledAt) : formatDateTime(current.checkOutDate, current.stayProfile?.checkOutTime)} (grace ${chargeQuote.graceMinutes} min). Choose the charge, or waive it with a reason.` : "",
            ].filter(Boolean).join(" ") || undefined}
            onChangeDates={canChangeDates ? () => { setDatesPassed(true); setActiveAction("change-dates"); } : undefined}
            onCancel={() => setActiveAction(null)}
            onSubmit={(input) => confirmStay("check-out", input, chargeQuote, actionScope)}
          />
        )}
        {activeAction === "extend" && (
          <ExtendStayForm
            booking={current}
            loading={extendMutation.isPending}
            error={extendMutation.error}
            onCancel={() => setActiveAction(null)}
            scope={actionScope}
            onSubmit={(checkOutDate, lines) => setPendingConfirm({
              title: "Extend the stay?", lines: [["Guest", current.guestName], ["Room(s)", roomsLabel(actionScope)], ...lines], confirmLabel: "Yes, extend",
              run: () => extendMutation.mutate({ checkOutDate, bookingRoomIds: actionScope }, settle),
            })}
          />
        )}
        {activeAction === "confirm-room" && (
          <ConfirmRoomForm
            booking={current}
            propertyId={propertyId}
            loading={statusMutation.isPending}
            error={statusMutation.error}
            onCancel={() => setActiveAction(null)}
            onSubmit={(roomIds) => setPendingConfirm({
              title: "Confirm this booking?", confirmLabel: "Yes, confirm",
              lines: [["Guest", current.guestName], ["Stay", `${formatDateTime(current.checkInDate)} → ${formatDateTime(current.checkOutDate)}`], ["Rooms allotted", String(roomIds.length)]],
              note: `${current.guestName} will be notified.`,
              run: () => statusMutation.mutate({ status: "CONFIRMED", roomIds }, settle),
            })}
          />
        )}
        {activeAction === "change-room" && (
          <ChangeRoomForm
            booking={current}
            propertyId={propertyId}
            loading={roomChangeMutation.isPending}
            error={roomChangeMutation.error}
            onCancel={() => setActiveAction(null)}
            scopeRoomId={actionScope?.[0]}
            onSubmit={(input, lines) => setPendingConfirm({
              title: "Change the room?", confirmLabel: "Yes, change room",
              lines: [["Guest", current.guestName], ...lines],
              run: () => roomChangeMutation.mutate({ ...input, bookingRoomId: actionScope?.[0] }, settle),
            })}
          />
        )}
        {activeAction === "exchange" && (
          <ExchangeRoomForm
            booking={current}
            propertyId={propertyId}
            loading={exchangeMutation.isPending}
            error={exchangeMutation.error}
            onCancel={() => setActiveAction(null)}
            scopeRoomId={actionScope?.[0]}
            onSubmit={(withBookingId, withBookingRoomId) => setPendingConfirm({
              title: "Exchange the rooms?", confirmLabel: "Yes, exchange",
              lines: [["Guest", current.guestName], ["This guest's room", roomsLabel(actionScope)], ["Swap with", "The booking selected above"]],
              note: "Both bookings move rooms and both room charges are recalculated.",
              run: () => exchangeMutation.mutate({ withBookingId, withBookingRoomId, bookingRoomId: actionScope?.[0] }, settle),
            })}
          />
        )}
        {activeAction === "add-room" && (
          <AddRoomForm
            booking={current}
            propertyId={propertyId}
            loading={addRoomMutation.isPending}
            error={addRoomMutation.error}
            onCancel={() => setActiveAction(null)}
            onSubmit={(roomId) => setPendingConfirm({
              title: "Add a room to this booking?", confirmLabel: "Yes, add room",
              lines: [["Guest", current.guestName], ["Stay", `${formatDateTime(current.checkInDate)} → ${formatDateTime(current.checkOutDate)}`]],
              note: "The new room's charge for these nights is added to the total.",
              run: () => addRoomMutation.mutate(roomId, settle),
            })}
          />
        )}
        {activeAction === "rate-plan" && (
          <RatePlanForm
            booking={current}
            propertyId={propertyId}
            loading={ratePlanMutation.isPending}
            error={ratePlanMutation.error}
            onCancel={() => setActiveAction(null)}
            onSubmit={(input) => setPendingConfirm({
              title: "Change the rate plan?", confirmLabel: "Yes, change rate plan",
              lines: [["Guest", current.guestName], ["Current plan", current.ratePlan?.name ?? "None"]],
              note: "Every room still on the booking is repriced.",
              run: () => ratePlanMutation.mutate(input, settle),
            })}
          />
        )}
        <h4 className="bd-section-title">Details</h4>
        <section className="booking-detail-grid">
          <BookingInfoPanel title="Guest" icon={CircleUserRound} rows={[
            ["Name", current.guestName],
            ["Phone", current.guestPhone],
            ["Email", current.guestEmail || "Not provided"],
          ]} />
          <BookingInfoPanel title="Room" icon={BedDouble} rows={[
            ["Room type", current.roomType?.name ?? "Not provided"],
            ["Room", (current.roomsCount ?? 1) > 1 && current.allottedRooms?.length ? current.allottedRooms.map((entry) => entry.room.name).join(", ") : current.room?.name ?? "Not yet allotted"],
            ["Number", current.room?.roomNumber || "Unassigned"],
            ["Base price", current.room?.basePrice === undefined ? "Not provided" : currency.format(Number(current.room.basePrice))],
          ]} />
          <BookingInfoPanel title="Booking" icon={ReceiptText} rows={[
            ["Source", (current.source ?? "DIRECT_SITE").replaceAll("_", " ")],
            ["Rate plan", current.ratePlan ? `${current.ratePlan.name} (${current.ratePlan.amountType === "PERCENTAGE" ? `${current.ratePlan.amountValue}%` : currency.format(Number(current.ratePlan.amountValue))})` : "None"],
            ["Created", current.createdAt ? formatDateTime(current.createdAt) : "Not provided"],
            ["Responded", current.respondedAt ? formatDateTime(current.respondedAt) : "Awaiting response"],
          ]} />
          <BookingInfoPanel title="Stay" icon={LogIn} rows={[
            ["Checked in", current.checkedInAt ? formatDateTime(current.checkedInAt) : "Not yet"],
            ["Checked out", current.checkedOutAt ? formatDateTime(current.checkedOutAt) : "Not yet"],
            ...(current.earlyCheckinType && current.earlyCheckinType !== "NONE" ? [["Early check-in", `${stayChargeLabel(current.earlyCheckinType)} · ${currency.format(Number(current.earlyCheckinCharge ?? 0))}`] as [string, string]] : []),
            ...(current.lateCheckoutType && current.lateCheckoutType !== "NONE" ? [["Late checkout", `${stayChargeLabel(current.lateCheckoutType)} · ${currency.format(Number(current.lateCheckoutCharge ?? 0))}`] as [string, string]] : []),
          ]} />
        </section>

        <section className="booking-line-section">
          <div className="booking-line-section__head">
            <h4><Users size={16} /> Guests</h4>
            {canEditGuests && !editingGuests && <button type="button" className="booking-line-section__add" onClick={() => setEditingGuests(true)}><Pencil size={13} /> Edit</button>}
          </div>
          <div className="booking-line-list">
            <div className="booking-line-item"><div><strong>Adults</strong></div><span className="num">{current.adults ?? current.noOfGuests}</span></div>
            <div className="booking-line-item"><div><strong>Children</strong></div><span className="num">{current.children ?? 0}</span></div>
          </div>
          {editingGuests && (
            <GuestCountForm
              adults={current.adults ?? current.noOfGuests}
              children={current.children ?? 0}
              loading={occupancyMutation.isPending}
              error={occupancyMutation.error}
              onCancel={() => setEditingGuests(false)}
              onSubmit={(input) => setPendingConfirm({
                title: "Change the guest count?", confirmLabel: "Yes, save guests",
                lines: [
                  ["Adults", `${current.adults ?? current.noOfGuests} → ${input.adults}`],
                  ["Children", `${current.children ?? 0} → ${input.children}`],
                  ...(input.extraCharge !== undefined ? [["Extra guest charge", currency.format(input.extraCharge)] as [string, string]] : []),
                ],
                run: () => occupancyMutation.mutate(input, settle),
              })}
            />
          )}
          <GuestRegister
            booking={current}
            canEdit={access.can("CHECK_IN_OUT") && !isClosed}
            canSeeIds={access.can("GUEST_IDS")}
            required={Boolean(current.stayProfile?.requireGuestDetails)}
            openForm={openGuestForm}
            onFormOpened={() => setOpenGuestForm(false)}
            onUpdated={(updated) => { setCurrent(updated); void queryClient.invalidateQueries({ queryKey: ["bookings"] }); }}
          />
        </section>

        <section className="booking-line-section">
          <div className="booking-line-section__head">
            <h4><Utensils size={16} /> Services</h4>
            {canChargesServices && <button type="button" className="booking-line-section__add" onClick={() => setAddingService(true)}><Plus size={13} /> Add</button>}
          </div>
          {current.services?.length ? (
            <div className="booking-line-list">
              {current.services.map((line) => (
                <div className={line.reversedAt ? "booking-line-item is-reversed" : "booking-line-item"} key={line.id}>
                  <div>
                    <span className="booking-line-item__title-row">
                      <strong>{line.title}</strong>
                      <span className={`service-tag ${line.includedInRatePlan ? "service-tag--rate-plan" : "service-tag--added"}`}>{line.includedInRatePlan ? "Rate plan" : "Added"}</span>
                    </span>
                    <small>{currency.format(Number(line.price))} × {line.quantity}{Number(line.taxAmount ?? 0) > 0 ? ` + GST ${Number(line.taxPercent ?? 0)}% (${currency.format(Number(line.taxAmount))})` : ""}</small>
                    {line.reversedAt && <small className="booking-line-item__reversal">Reversed: {line.reversalReason}</small>}
                  </div>
                  <strong className="booking-line-item__value">{currency.format(Number(line.price) * line.quantity + Number(line.taxAmount ?? 0))}</strong>
                  {canChargesServices && !line.reversedAt && (
                    <span className="booking-line-item__actions">
                      <button type="button" aria-label={`Increase ${line.title} quantity`} disabled={updateServiceQtyMutation.isPending} onClick={() => updateServiceQtyMutation.mutate({ lineId: line.id, quantity: line.quantity + 1 })}>+</button>
                      <button type="button" aria-label={`Decrease ${line.title} quantity`} disabled={updateServiceQtyMutation.isPending || line.quantity <= 1} onClick={() => updateServiceQtyMutation.mutate({ lineId: line.id, quantity: line.quantity - 1 })}>-</button>
                      <button type="button" aria-label={`Reverse ${line.title}`} title="Reverse" disabled={removeServiceMutation.isPending} onClick={() => setPendingConfirm({
                        title: "Reverse this service?", danger: true, confirmLabel: "Yes, reverse", reasonLabel: "Reason for reversing",
                        lines: [["Service", `${line.title} × ${line.quantity}`], ["Total goes down by", currency.format(Number(line.price) * line.quantity + Number(line.taxAmount ?? 0))]],
                        note: "The line stays on the bill, struck through, with your reason.",
                        run: (reason) => removeServiceMutation.mutate({ lineId: line.id, reason: reason ?? "" }, settle),
                      })}><Undo2 size={14} /></button>
                    </span>
                  )}
                </div>
              ))}
            </div>
          ) : <p className="booking-line-section__empty">No services added to this booking.</p>}
          {addingService && (
            <AddServiceForm propertyId={propertyId} loading={addServiceMutation.isPending} error={addServiceMutation.error} onCancel={() => setAddingService(false)} onSubmit={(input) => setPendingConfirm({
              title: "Add this service?", confirmLabel: "Yes, add service", lines: [["Quantity", String(input.quantity)]], note: "The service price (with tax) is added to the total.",
              run: () => addServiceMutation.mutate(input, settle),
            })} />
          )}
        </section>

        <section className="booking-line-section">
          <div className="booking-line-section__head">
            <h4><ReceiptText size={16} /> Extra charges</h4>
            {canChargesServices && <button type="button" className="booking-line-section__add" onClick={() => setAddingCharge(true)}><Plus size={13} /> Add</button>}
          </div>
          {current.charges?.length ? (
            <div className="booking-line-list">
              {current.charges.map((charge) => (
                <div className={charge.reversedAt ? "booking-line-item is-reversed" : "booking-line-item"} key={charge.id}>
                  <div><strong>{charge.label}</strong>{charge.reversedAt && <small className="booking-line-item__reversal">Reversed: {charge.reversalReason}</small>}</div>
                  <strong className="booking-line-item__value">{currency.format(Number(charge.amount))}</strong>
                  {canChargesServices && !charge.reversedAt && (
                    <span className="booking-line-item__actions">
                      <button type="button" aria-label={`Reverse ${charge.label}`} title="Reverse" disabled={removeChargeMutation.isPending} onClick={() => setPendingConfirm({
                        title: "Reverse this charge?", danger: true, confirmLabel: "Yes, reverse", reasonLabel: "Reason for reversing",
                        lines: [["Charge", charge.label], ["Total goes down by", currency.format(Number(charge.amount))]],
                        note: "The line stays on the bill, struck through, with your reason.",
                        run: (reason) => removeChargeMutation.mutate({ chargeId: charge.id, reason: reason ?? "" }, settle),
                      })}><Undo2 size={14} /></button>
                    </span>
                  )}
                </div>
              ))}
            </div>
          ) : <p className="booking-line-section__empty">No extra charges added.</p>}
          {addingCharge && (
            <AddChargeForm loading={addChargeMutation.isPending} error={addChargeMutation.error} onCancel={() => setAddingCharge(false)} onSubmit={(input) => setPendingConfirm({
              title: "Add this charge?", confirmLabel: "Yes, add charge", lines: [["Charge", input.label], ["Amount", currency.format(input.amount)]],
              run: () => addChargeMutation.mutate(input, settle),
            })} />
          )}
        </section>

        <section className="booking-line-section">
          <div className="booking-line-section__head">
            <h4><Percent size={16} /> Discounts</h4>
            {canDiscounts && <button type="button" className="booking-line-section__add" onClick={() => setAddingDiscount(true)}><Plus size={13} /> Add</button>}
          </div>
          {current.discounts?.length ? (
            <div className="booking-line-list">
              {current.discounts.map((discount) => (
                <div className={discount.reversedAt ? "booking-line-item is-reversed" : "booking-line-item"} key={discount.id}>
                  <div><strong>{discount.label}</strong><small>{discount.reason ?? ""}</small>{discount.reversedAt && <small className="booking-line-item__reversal">Reversed: {discount.reversalReason}</small>}</div>
                  <strong className="booking-line-item__value">-{currency.format(Number(discount.amount))}</strong>
                  {canDiscounts && discount.source === "MANUAL" && !discount.reversedAt && (
                    <span className="booking-line-item__actions">
                      <button type="button" aria-label={`Reverse ${discount.label}`} title="Reverse" disabled={removeDiscountMutation.isPending} onClick={() => setPendingConfirm({
                        title: "Reverse this discount?", danger: true, confirmLabel: "Yes, reverse", reasonLabel: "Reason for reversing",
                        lines: [["Discount", discount.label], ["Given for", discount.reason ?? "-"], ["Total goes up by", currency.format(Number(discount.amount))]],
                        note: "The discount stays on the bill, struck through, with your reason.",
                        run: (reason) => removeDiscountMutation.mutate({ discountId: discount.id, reason: reason ?? "" }, settle),
                      })}><Undo2 size={14} /></button>
                    </span>
                  )}
                </div>
              ))}
            </div>
          ) : <p className="booking-line-section__empty">No discounts.</p>}
          {discountNotice && <div className="form-warning">{discountNotice}</div>}
          {(current.discountRequests ?? []).filter((request) => request.status === "PENDING" || Date.now() - new Date(request.decidedAt ?? request.createdAt).getTime() < 7 * 86_400_000).map((request) => (
            <div key={request.id} className={`discount-request discount-request--${request.status.toLowerCase()}`}>
              <div>
                <strong>{request.status === "PENDING" ? "Waiting for approval" : request.status === "APPROVED" ? "Approved" : "Rejected"}: {currency.format(Number(request.amount))}{request.type === "PERCENT" ? ` (${Number(request.value)}%)` : ""}</strong>
                <small>Asked by {request.requestedBy.name} · {request.reason}</small>
                {request.status !== "PENDING" && <small>{request.decidedBy?.name ?? "Owner"}{request.decisionNote ? ` - ${request.decisionNote}` : ""}</small>}
              </div>
              {request.status === "PENDING" && access.canManageMembers && canEditExtras && (
                <span className="discount-request__actions">
                  <Button variant="secondary" disabled={decideDiscountMutation.isPending} onClick={() => setPendingConfirm({
                    title: "Reject this discount?", danger: true, confirmLabel: "Yes, reject", reasonLabel: `Note for ${request.requestedBy.name}`,
                    lines: [["Discount", currency.format(Number(request.amount))], ["Asked by", request.requestedBy.name], ["Their reason", request.reason]],
                    run: (note) => decideDiscountMutation.mutate({ requestId: request.id, approve: false, note }, settle),
                  })}>Reject</Button>
                  <Button disabled={decideDiscountMutation.isPending} onClick={() => setPendingConfirm({
                    title: "Approve this discount?", confirmLabel: "Yes, approve",
                    lines: [["Discount", `${currency.format(Number(request.amount))}${request.type === "PERCENT" ? ` (${Number(request.value)}% of the current total)` : ""}`], ["Asked by", request.requestedBy.name], ["Reason", request.reason], ["New total", currency.format(Number(current.totalAmount) - Number(request.amount))]],
                    run: () => decideDiscountMutation.mutate({ requestId: request.id, approve: true }, settle),
                  })}>Approve</Button>
                </span>
              )}
            </div>
          ))}
          {addingDiscount && (
            <AddDiscountForm total={Number(current.totalAmount)} limit={access.discountLimit} loading={addDiscountMutation.isPending} error={addDiscountMutation.error} onCancel={() => setAddingDiscount(false)} onSubmit={(input, amount) => setPendingConfirm({
              title: overDiscountLimit(access.discountLimit, amount, Number(current.totalAmount)) ? "Send this discount for approval?" : "Give this discount?",
              confirmLabel: overDiscountLimit(access.discountLimit, amount, Number(current.totalAmount)) ? "Yes, ask the owner" : "Yes, give discount",
              lines: [["Discount", input.type === "PERCENT" ? `${input.value}% (${currency.format(amount)})` : currency.format(amount)], ["Reason", input.reason], ["New total", currency.format(Number(current.totalAmount) - amount)]],
              run: () => addDiscountMutation.mutate(input, settle),
            })} />
          )}
        </section>

        <section className="booking-pricing-chart">
          <h4><ReceiptText size={16} /> Pricing breakdown</h4>
          <div className="booking-pricing-chart__rows">
            {pricedRooms.length ? pricedRooms.map((room) => (
              <div key={room.id}><span>{roomStayLabel(room)} ({Math.max(1, Math.round((new Date(room.checkOutDate).getTime() - new Date(room.checkInDate).getTime()) / 86_400_000))} night(s){current.ratePlan ? ` · ${current.ratePlan.name}` : ""})</span><strong>{currency.format(Number(room.amount))}</strong></div>
            )) : <div><span>Room ({nights} night{nights === 1 ? "" : "s"} × {currency.format(roomBasePrice)})</span><strong>{currency.format(roomSubtotal)}</strong></div>}
            {!pricedRooms.length && current.ratePlan && (
              <div><span>Rate plan — {current.ratePlan.name} ({current.ratePlan.amountType === "PERCENTAGE" ? `${current.ratePlan.amountValue}%` : "flat"})</span><strong>{ratePlanAdjustment >= 0 ? "+" : ""}{currency.format(ratePlanAdjustment)}</strong></div>
            )}
            {servicesSubtotal > 0 && <div><span>Services</span><strong>{currency.format(servicesSubtotal)}</strong></div>}
            {servicesTax > 0 && <div><span>GST on services</span><strong>{currency.format(servicesTax)}</strong></div>}
            {earlyCheckinCharge > 0 && <div><span>Early check-in charge</span><strong>{currency.format(earlyCheckinCharge)}</strong></div>}
            {lateCheckoutCharge > 0 && <div><span>Late checkout charge</span><strong>{currency.format(lateCheckoutCharge)}</strong></div>}
            {extraChargesTotal > 0 && <div><span>Extra charges</span><strong>{currency.format(extraChargesTotal)}</strong></div>}
            {liveDiscounts.map((discount) => (
              <div key={discount.id} className="booking-pricing-chart__discount"><span>{discount.label}{discount.reason ? ` - ${discount.reason}` : ""}</span><strong>-{currency.format(Number(discount.amount))}</strong></div>
            ))}
            {roomTaxAdded > 0 && <div><span>GST on rooms</span><strong>{currency.format(roomTaxAdded)}</strong></div>}
            {Math.abs(otherAdjustment) > 0.5 && <div><span>Other adjustment</span><strong>{otherAdjustment >= 0 ? "+" : ""}{currency.format(otherAdjustment)}</strong></div>}
            <div className="booking-pricing-chart__total"><span>Total</span><strong>{currency.format(Number(current.totalAmount))}</strong></div>
            {current.gstInclusive && roomTax > 0 && <div className="booking-pricing-chart__note"><span>Includes GST on rooms</span><span>{currency.format(roomTax)}</span></div>}
          </div>
        </section>

        <DepositPanel booking={current} due={due} canTake={access.can("PAYMENTS")} canSettle={access.can("REFUNDS")} onUpdated={applyUpdate} />

        {current.bookingStatus !== "PENDING" && (
          <InvoicePanel booking={current} canEditBilling={access.can("BOOKINGS_MANAGE")} canCredit={access.can("REFUNDS")} onUpdated={applyUpdate} />
        )}

        <section className="booking-payment-card">
          <div className="booking-payment-card__head">
            <span className={`status status--${(current.paymentStatus ?? "UNPAID").toLowerCase()}`}>{statusLabel(current.paymentStatus ?? "UNPAID")}</span>
          </div>
          <div className="booking-payment-card__grid">
            <div><small>Total</small><strong>{currency.format(Number(current.totalAmount))}</strong></div>
            <div><small>Paid</small><strong>{currency.format(paidAmount)}</strong></div>
            <div><small>Refund</small><strong>{currency.format(refundAmount)}</strong></div>
            <div className={due > 0 ? "booking-payment-card__due--pending" : due < 0 ? "booking-payment-card__due--refund" : "booking-payment-card__due--settled"}>
              <small>{due < 0 ? "Refund due" : "Due"}</small><strong>{currency.format(Math.abs(due))}</strong>
            </div>
          </div>
        </section>

        <BookingHistory bookingId={current.id} refreshKey={`${current.version ?? 0}-${current.payments?.length ?? 0}`} />


      </div>
      {(mainAction || moreActions.length > 0 || pendingConfirm) && (
        <section className="bd-actions" aria-label="Booking actions">
          {pendingConfirm ? (
            <ConfirmPanel
              key={pendingConfirm.title}
              confirm={pendingConfirm}
              busy={[statusMutation, noShowMutation, checkInMutation, checkOutMutation, changeDatesMutation, extendMutation, roomChangeMutation, exchangeMutation, addRoomMutation, removeRoomMutation, ratePlanMutation, recordPaymentMutation, recordRefundMutation, occupancyMutation, addServiceMutation, removeServiceMutation, addChargeMutation, removeChargeMutation, addDiscountMutation, removeDiscountMutation, decideDiscountMutation].some((mutation) => mutation.isPending)}
              onCancel={() => setPendingConfirm(null)}
            />
          ) : (
            <>
              <div className="bd-actions__row">
                {secondaryAction && <Button variant="secondary" onClick={secondaryAction.onSelect}><secondaryAction.icon size={16} /> {secondaryAction.label}</Button>}
                {moreActions.length > 0 && (
                  <div className="bd-more" ref={moreRef}>
                    <Button variant="secondary" aria-haspopup="menu" aria-expanded={moreOpen} onClick={() => setMoreOpen((open) => !open)}>More actions <ChevronDown size={16} /></Button>
                    {moreOpen && (
                      <div className="menu-panel bd-more__panel" role="menu">
                        {moreActions.map((item) => (
                          <button key={item.key} type="button" role="menuitem" className={item.danger ? "is-danger" : undefined} disabled={item.disabled} onClick={() => { setMoreOpen(false); item.onSelect(); }}>
                            <item.icon size={16} /> {item.label}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
              {mainAction && <Button className="bd-actions__primary" disabled={mainAction.disabled} onClick={mainAction.onSelect}><mainAction.icon size={18} /> {mainAction.label}</Button>}
            </>
          )}
        </section>
      )}
    </ResourceDialog>
  );
}

function StayChargeForm({ title, chargeLabel, quote, loading, error, banner, onChangeDates, onFillGuests, onCancel, onSubmit }: {
  title: string;
  chargeLabel: string;
  quote: StayChargeQuote | null;
  loading: boolean;
  error: Error | null;
  banner?: string;
  onChangeDates?: () => void;
  onFillGuests?: () => void;
  onCancel: () => void;
  onSubmit: (input: StayCharge) => void;
}) {
  const [type, setType] = useState<StayChargeType>(quote?.applicable ? quote.suggestedType : "NONE");
  const [amount, setAmount] = useState("");
  const [reason, setReason] = useState("");
  // Same-day check-out: price of the day and the policy for further nights.
  const [dayType, setDayType] = useState<DayUseType>("FULL");
  const [dayAmount, setDayAmount] = useState("");
  const [remainingType, setRemainingType] = useState<RemovedNightsPolicy["type"]>(quote?.defaultRemainingPolicy ?? "REFUND");
  const [keepAmount, setKeepAmount] = useState("");
  const [remainingReason, setRemainingReason] = useState("");
  const blocked = quote ? !quote.allowed : false;
  const applicable = Boolean(quote?.applicable);
  const sameDay = Boolean(quote?.sameDay);
  const charge = !applicable ? 0 : type === "FULL" ? quote!.fullCharge : type === "HALF_DAY" ? quote!.halfDayCharge : type === "MANUAL" ? Number(amount || 0) : 0;
  const dayCharge = !sameDay ? 0 : dayType === "FULL" ? quote!.dayUseFull ?? 0 : dayType === "HALF_DAY" ? quote!.dayUseHalf ?? 0 : Number(dayAmount || 0);
  const waiving = (applicable && charge <= 0) || (sameDay && dayCharge <= 0 && (dayType !== "MANUAL" || dayAmount !== ""));
  const remainingNights = sameDay ? quote!.remainingNights ?? 0 : 0;
  const remainingAmount = quote?.remainingAmount ?? 0;
  const retained = !remainingNights ? 0 : remainingType === "CHARGE" ? remainingAmount : remainingType === "MANUAL" ? Math.min(remainingAmount, Math.max(0, Number(keepAmount || 0))) : 0;
  const remainingNeedsReason = remainingNights > 0 && quote?.refundable === false && retained < remainingAmount - 0.005;
  const canSubmit = !blocked && (!waiving || reason.trim().length >= 3) && (type !== "MANUAL" || !applicable || amount !== "")
    && (!sameDay || dayType !== "MANUAL" || dayAmount !== "")
    && (!remainingNights || ((remainingType !== "MANUAL" || keepAmount !== "") && (!remainingNeedsReason || remainingReason.trim().length >= 3)));

  return (
    <section className="booking-inline-form">
      <h4>{title}</h4>
      {blocked && (
        <div className="form-error">
          {quote?.blockedReason}
          {onChangeDates && <> <button type="button" className="text-link" onClick={onChangeDates}>Change dates</button></>}
          {onFillGuests && <> <button type="button" className="text-link" onClick={onFillGuests}>Fill guest details</button></>}
        </div>
      )}
      {!blocked && banner && <div className="form-warning">{banner}</div>}
      {!blocked && applicable && (
        <div className="stay-charge-options">
          <label><input type="radio" name="stayChargeType" checked={type === "HALF_DAY"} onChange={() => setType("HALF_DAY")} /> Half day ({quote!.halfDayPercent}% · {currency.format(quote!.halfDayCharge)})</label>
          <label><input type="radio" name="stayChargeType" checked={type === "FULL"} onChange={() => setType("FULL")} /> Full night ({currency.format(quote!.fullCharge)})</label>
          <label><input type="radio" name="stayChargeType" checked={type === "MANUAL"} onChange={() => setType("MANUAL")} /> Manual amount</label>
          <label><input type="radio" name="stayChargeType" checked={type === "NONE"} onChange={() => setType("NONE")} /> No charge (waive)</label>
        </div>
      )}
      {!blocked && sameDay && (
        <div className="removed-nights">
          <strong>Leaving on the day of arrival - what does today cost{(quote!.sameDayRooms ?? 1) > 1 ? " (per room)" : ""}?</strong>
          <div className="stay-charge-options">
            <label><input type="radio" name="dayUseType" checked={dayType === "FULL"} onChange={() => setDayType("FULL")} /> Full night ({currency.format(quote!.dayUseFull ?? 0)})</label>
            <label><input type="radio" name="dayUseType" checked={dayType === "HALF_DAY"} onChange={() => setDayType("HALF_DAY")} /> Half day ({quote!.halfDayPercent}% · {currency.format(quote!.dayUseHalf ?? 0)})</label>
            <label><input type="radio" name="dayUseType" checked={dayType === "MANUAL"} onChange={() => setDayType("MANUAL")} /> Manual amount</label>
          </div>
          {dayType === "MANUAL" && (
            <div className="property-form-grid">
              <label>Charge for today<input type="number" min={0} step="0.01" value={dayAmount} onChange={(event) => setDayAmount(event.target.value)} /></label>
            </div>
          )}
          {remainingNights > 0 && (
            <>
              <strong>Remaining {remainingNights} booked night(s) ({currency.format(remainingAmount)}) - {quote!.refundable ? "refundable rate plan" : "non-refundable rate plan"}</strong>
              <div className="stay-charge-options">
                <label><input type="radio" name="remainingNights" checked={remainingType === "REFUND"} onChange={() => setRemainingType("REFUND")} /> Refund remaining nights</label>
                <label><input type="radio" name="remainingNights" checked={remainingType === "CHARGE"} onChange={() => setRemainingType("CHARGE")} /> Charge remaining nights ({currency.format(remainingAmount)})</label>
                <label><input type="radio" name="remainingNights" checked={remainingType === "MANUAL"} onChange={() => setRemainingType("MANUAL")} /> Manual adjustment</label>
              </div>
              {remainingType === "MANUAL" && (
                <div className="property-form-grid">
                  <label>Amount to keep (max {currency.format(remainingAmount)})<input type="number" min={0} max={remainingAmount} step="0.01" value={keepAmount} onChange={(event) => setKeepAmount(event.target.value)} /></label>
                </div>
              )}
              {remainingNeedsReason && (
                <div className="property-form-grid">
                  <label className="property-form-grid__wide">Reason for not charging (non-refundable)<input value={remainingReason} maxLength={300} onChange={(event) => setRemainingReason(event.target.value)} /></label>
                </div>
              )}
            </>
          )}
        </div>
      )}
      {!blocked && applicable && type === "MANUAL" && (
        <div className="property-form-grid">
          <label>{chargeLabel}{quote!.chargeableRooms > 1 ? " (per room)" : ""}<input type="number" min={0} step="0.01" value={amount} onChange={(event) => setAmount(event.target.value)} /></label>
        </div>
      )}
      {!blocked && waiving && (
        <div className="property-form-grid">
          <label className="property-form-grid__wide">Reason for waiving the {sameDay && !applicable ? "charge for today" : chargeLabel.toLowerCase()}<input value={reason} maxLength={300} placeholder="e.g. regular guest, room was ready" onChange={(event) => setReason(event.target.value)} /></label>
        </div>
      )}
      {error && <div className="form-error">{error.message}</div>}
      <div className="booking-inline-form__actions">
        <Button type="button" variant="ghost" onClick={onCancel}>Cancel</Button>
        <Button type="button" loading={loading} disabled={!canSubmit} onClick={() => onSubmit({
          type: applicable ? type : "NONE",
          charge: applicable && type === "MANUAL" ? Number(amount || 0) : undefined,
          waiverReason: waiving ? reason.trim() : undefined,
          ...(sameDay ? {
            dayUseType: dayType,
            dayUseCharge: dayType === "MANUAL" ? Number(dayAmount || 0) : undefined,
            removedNightsPolicy: remainingNights ? { type: remainingType, amount: remainingType === "MANUAL" ? retained : undefined, reason: remainingReason.trim() || undefined } : undefined,
          } : {}),
        })}>Continue</Button>
      </div>
    </section>
  );
}

function RecordPaymentForm({ balance, loading, error, onCancel, onSubmit }: {
  balance: number;
  loading: boolean;
  error: Error | null;
  onCancel: () => void;
  onSubmit: (input: { amount: number; paymentMode: "CASH" | "UPI" | "CARD" | "BANK_TRANSFER" | "OTHER"; transactionRef?: string; allowDuplicateRef?: boolean }) => void;
}) {
  const [amount, setAmount] = useState(String(balance));
  const [paymentMode, setPaymentMode] = useState<"CASH" | "UPI" | "CARD" | "BANK_TRANSFER" | "OTHER">("CASH");
  const [transactionRef, setTransactionRef] = useState("");
  const [allowDuplicateRef, setAllowDuplicateRef] = useState(false);
  // The server refuses a transaction reference already recorded at this property until the owner confirms it.
  const duplicate = error instanceof ApiError && error.status === 409 && /already recorded/i.test(error.message);

  return (
    <section className="booking-inline-form">
      <h4>Payment pending</h4>
      <div className="form-warning">A balance of {currency.format(balance)} is due on this booking.</div>
      <div className="property-form-grid">
        <label>Amount<input type="number" min={0.01} step="0.01" value={amount} onChange={(event) => setAmount(event.target.value)} /></label>
        <label>Payment mode
          <select value={paymentMode} onChange={(event) => setPaymentMode(event.target.value as typeof paymentMode)}>
            <option value="CASH">Cash</option>
            <option value="UPI">UPI</option>
            <option value="CARD">Card</option>
            <option value="BANK_TRANSFER">Bank transfer</option>
            <option value="OTHER">Other</option>
          </select>
        </label>
        <label className="property-form-grid__wide">Transaction reference<input value={transactionRef} onChange={(event) => { setTransactionRef(event.target.value); setAllowDuplicateRef(false); }} placeholder="Optional" /></label>
      </div>
      {error && <div className="form-error">{error.message}</div>}
      {duplicate && <label className="checkbox-row"><input type="checkbox" checked={allowDuplicateRef} onChange={(event) => setAllowDuplicateRef(event.target.checked)} /> This is a different payment - record it anyway</label>}
      <div className="booking-inline-form__actions">
        <Button type="button" variant="ghost" onClick={onCancel}>Cancel</Button>
        <Button type="button" loading={loading} disabled={!amount || Number(amount) <= 0 || (duplicate && !allowDuplicateRef)} onClick={() => onSubmit({ amount: Number(amount), paymentMode, transactionRef: transactionRef.trim() || undefined, allowDuplicateRef: duplicate && allowDuplicateRef ? true : undefined })}>Record payment</Button>
      </div>
    </section>
  );
}

function RecordRefundForm({ maxRefund, suggested, message, loading, error, onCancel, onSubmit }: {
  maxRefund: number;
  suggested: number;
  message: string;
  loading: boolean;
  error: Error | null;
  onCancel: () => void;
  onSubmit: (input: { amount: number; paymentMode: "CASH" | "UPI" | "CARD" | "BANK_TRANSFER" | "OTHER"; transactionRef?: string }) => void;
}) {
  const overpaid = maxRefund;
  const [amount, setAmount] = useState(String(suggested));
  const [paymentMode, setPaymentMode] = useState<"CASH" | "UPI" | "CARD" | "BANK_TRANSFER" | "OTHER">("CASH");
  const [transactionRef, setTransactionRef] = useState("");

  return (
    <section className="booking-inline-form">
      <h4>Record refund</h4>
      <div className="form-warning">{message}</div>
      <div className="property-form-grid">
        <label>Amount<input type="number" min={0.01} max={overpaid} step="0.01" value={amount} onChange={(event) => setAmount(event.target.value)} /></label>
        <label>Refund mode
          <select value={paymentMode} onChange={(event) => setPaymentMode(event.target.value as typeof paymentMode)}>
            <option value="CASH">Cash</option>
            <option value="UPI">UPI</option>
            <option value="CARD">Card</option>
            <option value="BANK_TRANSFER">Bank transfer</option>
            <option value="OTHER">Other</option>
          </select>
        </label>
        <label className="property-form-grid__wide">Transaction reference<input value={transactionRef} onChange={(event) => setTransactionRef(event.target.value)} placeholder="Optional" /></label>
      </div>
      {error && <div className="form-error">{error.message}</div>}
      <div className="booking-inline-form__actions">
        <Button type="button" variant="ghost" onClick={onCancel}>Cancel</Button>
        <Button type="button" loading={loading} disabled={!amount || Number(amount) <= 0 || Number(amount) > overpaid} onClick={() => onSubmit({ amount: Number(amount), paymentMode, transactionRef: transactionRef.trim() || undefined })}>Record refund</Button>
      </div>
    </section>
  );
}

function ChangeStayDatesForm({ booking, scope, notice, loading, error, onCancel, onSubmit }: {
  booking: Booking;
  scope?: string[];
  notice?: string;
  loading: boolean;
  error: Error | null;
  onCancel: () => void;
  onSubmit: (input: { checkInDate: string; checkOutDate: string; removedNightsPolicy?: RemovedNightsPolicy }, summary: [string, string][]) => void;
}) {
  // Business day: before the late-arrival cutoff, last night's date is still today for the desk.
  const today = businessDateIso(booking.stayProfile?.lateArrivalCutoff ?? "06:00");
  // The rooms this change applies to (a chosen few, or every room that can still move).
  const scoped = (booking.allottedRooms ?? []).filter((room) => (room.status === "CONFIRMED" || room.status === "CHECKED_IN") && (!scope?.length || scope.includes(room.id)));
  const startIn = (scoped[0]?.checkInDate ?? booking.checkInDate).slice(0, 10);
  const startOut = (scoped[0]?.checkOutDate ?? booking.checkOutDate).slice(0, 10);
  const [checkInDate, setCheckInDate] = useState(startIn);
  const [checkOutDate, setCheckOutDate] = useState(startOut);
  const lockCheckIn = scoped.length ? scoped.some((room) => room.status === "CHECKED_IN") : booking.bookingStatus === "CHECKED_IN";
  const changed = checkInDate !== startIn || checkOutDate !== startOut;
  const validRange = Boolean(checkInDate && checkOutDate && checkOutDate > checkInDate);
  // Availability and repricing are decided by the server for exactly these dates and these rooms, every time they change.
  const quote = useQuery({
    queryKey: ["booking-date-quote", booking.id, scope?.join(",") ?? "all", checkInDate, checkOutDate],
    queryFn: () => api<DateChangeQuote>(`/bookings/${booking.id}/dates/quote`, { method: "POST", body: { checkInDate, checkOutDate, bookingRoomIds: scope } }),
    enabled: changed && validRange,
    retry: false,
    staleTime: 0,
  });
  // Nights taken off: refunded by default on a refundable plan, kept on a non-refundable one; keeping less than
  // the default needs a reason.
  const removed = quote.data && !quote.isFetching ? quote.data.removedNights : 0;
  const [policyType, setPolicyType] = useState<RemovedNightsPolicy["type"] | null>(null);
  const [keepAmount, setKeepAmount] = useState("");
  const [policyReason, setPolicyReason] = useState("");
  const effectivePolicy = policyType ?? quote.data?.defaultPolicy ?? "REFUND";
  const removedAmount = quote.data?.removedAmount ?? 0;
  const retained = !removed ? 0 : effectivePolicy === "CHARGE" ? removedAmount : effectivePolicy === "MANUAL" ? Math.min(removedAmount, Math.max(0, Number(keepAmount || 0))) : 0;
  const defaultRetained = quote.data && !quote.data.refundable ? removedAmount : 0;
  const needsReason = removed > 0 && retained < defaultRetained - 0.005;
  const policyOk = !removed || ((effectivePolicy !== "MANUAL" || keepAmount !== "") && (!needsReason || policyReason.trim().length >= 3));
  const finalTotal = (quote.data?.newTotal ?? 0) + retained;
  const canSave = changed && validRange && quote.data?.available === true && policyOk;
  const target = scoped.length > 1 ? `${scoped.length} rooms` : scoped[0] ? roomStayLabel(scoped[0]) : "this booking";
  const contextLabel = quote.data?.removedContext === "EARLY_DEPARTURE" ? "Early departure" : quote.data?.removedContext === "NO_SHOW" ? "Missed night (no-show)" : "Shortened stay";
  const submit = () => {
    const policy: RemovedNightsPolicy | undefined = removed
      ? { type: effectivePolicy, amount: effectivePolicy === "MANUAL" ? retained : undefined, reason: policyReason.trim() || undefined }
      : undefined;
    onSubmit({ checkInDate, checkOutDate, removedNightsPolicy: policy }, [
      ["Dates", `${startIn} → ${startOut}  ⟶  ${checkInDate} → ${checkOutDate}`],
      ["Nights", `${quote.data!.previousNights} → ${quote.data!.newNights}`],
      ...(removed ? [[`${contextLabel} (${removed} night(s))`, retained > 0 ? `${currency.format(retained)} kept` : "Refunded"] as [string, string]] : []),
      ...(policy?.reason ? [["Reason", policy.reason] as [string, string]] : []),
      ["Total", `${currency.format(quote.data!.previousTotal)} → ${currency.format(finalTotal)}`],
    ]);
  };

  return (
    <section className="booking-inline-form">
      <h4>Change stay date - {target}</h4>
      {notice && <div className="form-warning">{notice}</div>}
      <div className="property-form-grid">
        <label>Check-in date<DateInput min={today} disabled={lockCheckIn} value={checkInDate} onChange={(event) => setCheckInDate(event.target.value)} /></label>
        <label>Check-out date<DateInput min={lockCheckIn && today > checkInDate ? today : checkInDate || today} value={checkOutDate} onChange={(event) => setCheckOutDate(event.target.value)} /></label>
      </div>
      {changed && !validRange && <div className="form-error">Check-out date must be after the check-in date.</div>}
      {changed && validRange && quote.isFetching && <div className="form-hint">Checking room availability...</div>}
      {quote.data && !quote.isFetching && !quote.data.available && (
        <div className="form-error">
          {quote.data.availabilityMessage ?? "Selected room is not available for the selected stay dates. Please select different dates or another room."}
          {quote.data.rooms && quote.data.rooms.length > 1 && <> Not free: {quote.data.rooms.filter((room) => !room.available).map((room) => room.roomName).join(", ")}.</>}
        </div>
      )}
      {quote.data?.available && !quote.isFetching && (
        <div className="form-warning">
          Room{scoped.length > 1 ? "s are" : " is"} available. {quote.data.previousNights} night(s) → {quote.data.newNights} night(s). New total: {currency.format(quote.data.previousTotal)} → {currency.format(finalTotal)}.
        </div>
      )}
      {quote.data?.available && !quote.isFetching && removed > 0 && (
        <div className="removed-nights">
          <strong>{contextLabel}: {removed} night(s) ({currency.format(removedAmount)}) - {quote.data.refundable ? "refundable rate plan" : "non-refundable rate plan"}</strong>
          <div className="stay-charge-options">
            <label><input type="radio" name="removedNights" checked={effectivePolicy === "REFUND"} onChange={() => setPolicyType("REFUND")} /> Refund remaining nights</label>
            <label><input type="radio" name="removedNights" checked={effectivePolicy === "CHARGE"} onChange={() => setPolicyType("CHARGE")} /> Charge remaining nights ({currency.format(removedAmount)})</label>
            <label><input type="radio" name="removedNights" checked={effectivePolicy === "MANUAL"} onChange={() => setPolicyType("MANUAL")} /> Manual adjustment</label>
          </div>
          {effectivePolicy === "MANUAL" && (
            <div className="property-form-grid">
              <label>Amount to keep (max {currency.format(removedAmount)})<input type="number" min={0} max={removedAmount} step="0.01" value={keepAmount} onChange={(event) => setKeepAmount(event.target.value)} /></label>
            </div>
          )}
          {needsReason && (
            <div className="property-form-grid">
              <label className="property-form-grid__wide">Reason for not charging (non-refundable)<input value={policyReason} maxLength={300} onChange={(event) => setPolicyReason(event.target.value)} /></label>
            </div>
          )}
        </div>
      )}
      {quote.error && <div className="form-error">{quote.error.message}</div>}
      {error && <div className="form-error">{error.message}</div>}
      <div className="booking-inline-form__actions">
        <Button type="button" variant="ghost" onClick={onCancel}>Cancel</Button>
        <Button type="button" loading={loading} disabled={!canSave} onClick={submit}>Continue</Button>
      </div>
    </section>
  );
}

function ExtendStayForm({ booking, scope, loading, error, onCancel, onSubmit }: {
  booking: Booking;
  scope?: string[];
  loading: boolean;
  error: Error | null;
  onCancel: () => void;
  onSubmit: (checkOutDate: string, summary: [string, string][]) => void;
}) {
  const scoped = (booking.allottedRooms ?? []).filter((room) => (room.status === "CONFIRMED" || room.status === "CHECKED_IN") && (!scope?.length || scope.includes(room.id)));
  const latestOut = scoped.length ? new Date(Math.max(...scoped.map((room) => new Date(room.checkOutDate).getTime()))) : new Date(booking.checkOutDate);
  const minCheckOutDate = new Date(latestOut.getTime() + 86_400_000).toISOString().slice(0, 10);
  const [checkOutDate, setCheckOutDate] = useState(minCheckOutDate);
  const quote = useQuery({
    queryKey: ["booking-date-quote", booking.id, "extend", scope?.join(",") ?? "all", checkOutDate],
    queryFn: () => api<DateChangeQuote>(`/bookings/${booking.id}/dates/quote`, { method: "POST", body: { checkOutDate, bookingRoomIds: scope, mode: "EXTEND" } }),
    enabled: checkOutDate >= minCheckOutDate,
    retry: false,
    staleTime: 0,
  });
  const canSave = checkOutDate >= minCheckOutDate && quote.data?.available === true;
  const target = scoped.length > 1 ? `${scoped.length} rooms` : scoped[0] ? roomStayLabel(scoped[0]) : "this booking";

  return (
    <section className="booking-inline-form">
      <h4>Extend stay - {target}</h4>
      <div className="property-form-grid">
        <label>New check-out date<DateInput min={minCheckOutDate} value={checkOutDate} onChange={(event) => setCheckOutDate(event.target.value)} /></label>
      </div>
      {quote.isFetching && <div className="form-hint">Checking room availability...</div>}
      {quote.data && !quote.isFetching && !quote.data.available && <div className="form-error">{quote.data.availabilityMessage}</div>}
      {quote.data?.available && !quote.isFetching && (
        <div className="form-warning">Available. Total is recalculated automatically: {currency.format(quote.data.previousTotal)} → {currency.format(quote.data.newTotal)}.</div>
      )}
      {quote.error && <div className="form-error">{quote.error.message}</div>}
      {error && <div className="form-error">{error.message}</div>}
      <div className="booking-inline-form__actions">
        <Button type="button" variant="ghost" onClick={onCancel}>Cancel</Button>
        <Button type="button" loading={loading} disabled={!canSave} onClick={() => onSubmit(checkOutDate, [
          ["New check-out", checkOutDate],
          ["Nights", `${quote.data!.previousNights} → ${quote.data!.newNights}`],
          ["Total", `${currency.format(quote.data!.previousTotal)} → ${currency.format(quote.data!.newTotal)}`],
        ])}>Continue</Button>
      </div>
    </section>
  );
}

function ConfirmRoomForm({ booking, propertyId, loading, error, onCancel, onSubmit }: {
  booking: Booking;
  propertyId: string;
  loading: boolean;
  error: Error | null;
  onCancel: () => void;
  onSubmit: (roomIds: string[]) => void;
}) {
  const rooms = useQuery({
    queryKey: ["inventory-rooms", propertyId, booking.checkInDate, booking.checkOutDate],
    queryFn: () => api<InventoryRoom[]>(`/inventory/rooms?propertyId=${propertyId}&date=${booking.checkInDate.slice(0, 10)}&checkOutDate=${booking.checkOutDate.slice(0, 10)}`),
  });
  const roomsNeeded = booking.roomsCount ?? 1;
  const [roomIds, setRoomIds] = useState<string[]>([]);
  // Auto-allot: the server's pick (clean rooms first, a group kept together) is pre-selected.
  const suggestion = useRoomSuggestion(booking, roomIds, setRoomIds);
  const types = allowedRoomTypes(booking);
  const isGroup = (booking.roomTypeLines?.length ?? 0) > 1;
  const options = (rooms.data ?? []).filter((room) => room.status === "AVAILABLE" && room.isBookable && (!types.length || (room.roomTypeId && types.includes(room.roomTypeId))));

  return (
    <section className="booking-inline-form">
      <h4>{roomsNeeded > 1 ? `Allot ${roomsNeeded} rooms to confirm` : "Allot a room to confirm"}</h4>
      <p className="booking-inline-form__hint">Requested: <strong>{needsText(booking)}</strong></p>
      {suggestion.data?.short.length ? <div className="form-warning">Not enough free rooms: {suggestion.data.short.map((entry) => `${entry.missing} ${entry.name}`).join(", ")} short for these dates.</div> : null}
      <RoomAllotmentPicker roomsNeeded={roomsNeeded} options={options} selected={roomIds} loading={rooms.isLoading} onChange={setRoomIds} suggested={suggestion.data?.roomIds} showType={isGroup} />
      {error && <div className="form-error">{error.message}</div>}
      <div className="booking-inline-form__actions">
        <Button type="button" variant="ghost" onClick={onCancel}>Cancel</Button>
        <Button type="button" loading={loading} disabled={roomIds.length !== roomsNeeded} onClick={() => onSubmit(roomIds)}>Confirm booking</Button>
      </div>
    </section>
  );
}

type RoomMoveInput = { roomId: string; pricing: "NEW_TARIFF" | "KEEP_RATE" | "MANUAL"; manualNightly?: number; reason?: string };
type RoomMoveQuote = {
  available: boolean; split: boolean; moveFrom: string; movedNights: number; keptNights: number; fromRoom: string; toRoom: string;
  bookedNightly: number; tariffNightly: number; newNightly: number; delta: number; newTotal: number;
};

// Once the guest has slept in the room, the move starts today: the nights already spent stay on the old room.
function ChangeRoomForm({ booking, propertyId, scopeRoomId, loading, error, onCancel, onSubmit }: {
  booking: Booking;
  propertyId: string;
  scopeRoomId?: string;
  loading: boolean;
  error: Error | null;
  onCancel: () => void;
  onSubmit: (input: RoomMoveInput, summary: [string, string][]) => void;
}) {
  const rooms = useQuery({ queryKey: ["rooms", propertyId], queryFn: () => api<Room[]>(`/rooms?propertyId=${propertyId}`) });
  const [roomId, setRoomId] = useState("");
  const [pricing, setPricing] = useState<RoomMoveInput["pricing"]>("NEW_TARIFF");
  const [manualNightly, setManualNightly] = useState("");
  const [reason, setReason] = useState("");
  // Rooms the booking still holds can't be picked; a room the guest moved out of earlier can.
  const holding = new Set((booking.allottedRooms ?? []).filter((room) => room.status === "CONFIRMED" || room.status === "CHECKED_IN").map((room) => room.roomId));
  const moving = (booking.allottedRooms ?? []).find((room) => room.id === scopeRoomId);
  const options = (rooms.data ?? []).filter((room) => !holding.has(room.id));
  const input: RoomMoveInput = { roomId, pricing, manualNightly: pricing === "MANUAL" && manualNightly !== "" ? Number(manualNightly) : undefined };
  const quote = useQuery({
    queryKey: ["room-move-quote", booking.id, scopeRoomId ?? "", roomId, pricing, input.manualNightly ?? ""],
    queryFn: () => api<RoomMoveQuote>(`/bookings/${booking.id}/room/quote`, { method: "POST", body: { ...input, bookingRoomId: scopeRoomId } }),
    enabled: Boolean(roomId) && (pricing !== "MANUAL" || manualNightly !== ""),
    retry: false,
    staleTime: 0,
  });
  const q = quote.data && !quote.isFetching ? quote.data : null;
  const canSave = Boolean(q?.available) && reason.trim().length >= 3;

  return (
    <section className="booking-inline-form">
      <h4>Change room{moving ? ` - ${roomStayLabel(moving)}` : ""}</h4>
      <div className="property-form-grid">
        <label>Move to
          <select value={roomId} onChange={(event) => setRoomId(event.target.value)}>
            <option value="">Select a room</option>
            {options.map((room) => <option key={room.id} value={room.id}>{room.name}{room.roomNumber ? ` - Room ${room.roomNumber}` : ""} · {currency.format(Number(room.basePrice ?? 0))}/night</option>)}
          </select>
        </label>
      </div>
      {roomId && (
        <div className="stay-charge-options">
          <label><input type="radio" name="movePricing" checked={pricing === "NEW_TARIFF"} onChange={() => setPricing("NEW_TARIFF")} /> New room's rate{q ? ` (${currency.format(q.tariffNightly)}/night)` : ""}</label>
          <label><input type="radio" name="movePricing" checked={pricing === "KEEP_RATE"} onChange={() => setPricing("KEEP_RATE")} /> Keep the booked rate{q ? ` (${currency.format(q.bookedNightly)}/night)` : ""} - e.g. free upgrade</label>
          <label><input type="radio" name="movePricing" checked={pricing === "MANUAL"} onChange={() => setPricing("MANUAL")} /> Enter a nightly rate</label>
        </div>
      )}
      {roomId && pricing === "MANUAL" && (
        <div className="property-form-grid">
          <label>Nightly rate for the new room<input type="number" min={0} step="0.01" value={manualNightly} onChange={(event) => setManualNightly(event.target.value)} /></label>
        </div>
      )}
      {roomId && (
        <div className="property-form-grid">
          <label className="property-form-grid__wide">Reason for the move<input value={reason} maxLength={300} placeholder="e.g. AC not working, guest request" onChange={(event) => setReason(event.target.value)} /></label>
        </div>
      )}
      {quote.isFetching && <div className="form-hint">Checking the room...</div>}
      {q && !q.available && <div className="form-error">{q.toRoom} is not free from {q.moveFrom}. Pick another room.</div>}
      {q?.available && (
        <div className="form-warning">
          {q.split
            ? `The guest has already stayed ${q.keptNights} night(s) in ${q.fromRoom} - those stay there. The move starts ${q.moveFrom}: ${q.movedNights} night(s) in ${q.toRoom} at ${currency.format(q.newNightly)}.`
            : `The whole stay (${q.movedNights} night(s)) moves to ${q.toRoom} at ${currency.format(q.newNightly)}/night.`}
          {" "}Total {q.delta === 0 ? "stays the same" : `${q.delta > 0 ? "goes up" : "goes down"} by ${currency.format(Math.abs(q.delta))}`}.
        </div>
      )}
      {quote.error && <div className="form-error">{quote.error.message}</div>}
      {error && <div className="form-error">{error.message}</div>}
      <div className="booking-inline-form__actions">
        <Button type="button" variant="ghost" onClick={onCancel}>Cancel</Button>
        <Button type="button" loading={loading} disabled={!canSave} onClick={() => q && onSubmit({ ...input, reason: reason.trim() }, [
          ["Reason", reason.trim()],
          ["From", q.fromRoom],
          ["To", q.toRoom],
          ...(q.split ? [["Nights kept in old room", String(q.keptNights)] as [string, string]] : []),
          ["Moving", `${q.movedNights} night(s) from ${q.moveFrom}`],
          ["Rate", `${currency.format(q.newNightly)}/night`],
          ["New total", currency.format(q.newTotal)],
          ...(q.split ? [["Old room", "Marked for cleaning"] as [string, string]] : []),
        ])}>Continue</Button>
      </div>
    </section>
  );
}

// Discount on an existing booking: amount or % of the current total, always with a reason.
// Same rule as the server: above either cap the discount waits for the main owner.
function overDiscountLimit(limit: DiscountLimit, amount: number, total: number) {
  return Boolean(limit && ((limit.amount !== null && amount > limit.amount + 0.005) || (limit.percent !== null && total > 0 && (amount / total) * 100 > limit.percent + 0.005)));
}

function AddDiscountForm({ total, limit, loading, error, onCancel, onSubmit }: {
  total: number;
  limit: DiscountLimit;
  loading: boolean;
  error: Error | null;
  onCancel: () => void;
  onSubmit: (input: { type: "AMOUNT" | "PERCENT"; value: number; reason: string }, amount: number) => void;
}) {
  const [type, setType] = useState<"AMOUNT" | "PERCENT">("AMOUNT");
  const [value, setValue] = useState("");
  const [reason, setReason] = useState("");
  const numeric = Number(value || 0);
  const amount = Math.round((type === "PERCENT" ? (total * numeric) / 100 : numeric) * 100) / 100;
  const valid = numeric > 0 && (type !== "PERCENT" || numeric <= 100) && amount <= total && reason.trim().length >= 3;

  return (
    <section className="booking-inline-form">
      <h4>Add discount</h4>
      <div className="property-form-grid">
        <label>Discount
          <span className="discount-input">
            <select aria-label="Discount type" value={type} onChange={(event) => setType(event.target.value as "AMOUNT" | "PERCENT")}>
              <option value="AMOUNT">₹</option>
              <option value="PERCENT">%</option>
            </select>
            <input type="number" min={0} step="0.01" value={value} onChange={(event) => setValue(event.target.value)} />
          </span>
        </label>
        <label>Reason<input value={reason} maxLength={300} placeholder="e.g. regular guest, AC not working" onChange={(event) => setReason(event.target.value)} /></label>
      </div>
      {limit && <div className="form-hint">Your limit: {[limit.percent !== null ? `${limit.percent}%` : "", limit.amount !== null ? currency.format(limit.amount) : ""].filter(Boolean).join(" and ")} per booking. Above that the main owner approves it.</div>}
      {numeric > 0 && <div className="form-hint">{amount > total ? `Can't be more than the total (${currency.format(total)}).` : `Total ${currency.format(total)} → ${currency.format(total - amount)}`}{overDiscountLimit(limit, amount, total) ? " - needs the owner's approval" : ""}</div>}
      {error && <div className="form-error">{error.message}</div>}
      <div className="booking-inline-form__actions">
        <Button type="button" variant="ghost" onClick={onCancel}>Cancel</Button>
        <Button type="button" loading={loading} disabled={!valid} onClick={() => onSubmit({ type, value: numeric, reason: reason.trim() }, amount)}>Continue</Button>
      </div>
    </section>
  );
}

function ExchangeRoomForm({ booking, propertyId, scopeRoomId, loading, error, onCancel, onSubmit }: {
  booking: Booking;
  propertyId: string;
  scopeRoomId?: string;
  loading: boolean;
  error: Error | null;
  onCancel: () => void;
  onSubmit: (withBookingId: string, withBookingRoomId?: string) => void;
}) {
  const bookings = useQuery({ queryKey: ["bookings", propertyId], queryFn: () => api<Booking[]>(`/bookings?propertyId=${propertyId}`) });
  const [choice, setChoice] = useState("");
  const moving = (booking.allottedRooms ?? []).find((room) => room.id === scopeRoomId);
  const movingRoomId = moving?.roomId ?? booking.room?.id;
  // Every room that is currently holding a stay, on this or another booking, other than the one being swapped.
  type ExchangeOption = { key: string; bookingId: string; bookingRoomId?: string; roomId: string; label: string };
  const options: ExchangeOption[] = (bookings.data ?? [])
    .filter((item) => ["CONFIRMED", "CHECKED_IN"].includes(item.bookingStatus))
    .flatMap((item): ExchangeOption[] => {
      const rooms = item.allottedRooms?.length
        ? item.allottedRooms.filter((room) => room.status === "CONFIRMED" || room.status === "CHECKED_IN").map((room) => ({ key: `${item.id}|${room.id}`, bookingId: item.id, bookingRoomId: room.id, roomId: room.roomId, label: `${item.bookingRef} - ${item.guestName} - ${roomStayLabel(room)}` }))
        : item.room ? [{ key: `${item.id}|`, bookingId: item.id, bookingRoomId: undefined, roomId: item.room.id, label: `${item.bookingRef} - ${item.guestName} - ${item.room.name}` }] : [];
      return rooms;
    })
    .filter((option) => option.roomId !== movingRoomId && option.bookingRoomId !== scopeRoomId);
  const picked = options.find((option) => option.key === choice);

  return (
    <section className="booking-inline-form">
      <h4>Exchange room{moving ? ` - ${roomStayLabel(moving)}` : ""} with another room</h4>
      <div className="property-form-grid">
        <label>Swap with
          <select value={choice} onChange={(event) => setChoice(event.target.value)}>
            <option value="">Select a booking room</option>
            {options.map((option) => <option key={option.key} value={option.key}>{option.label}</option>)}
          </select>
        </label>
      </div>
      <div className="form-hint">Both bookings' totals are recalculated automatically for their new rooms' tariff. If a balance is due afterwards, use Record payment.</div>
      {error && <div className="form-error">{error.message}</div>}
      <div className="booking-inline-form__actions">
        <Button type="button" variant="ghost" onClick={onCancel}>Cancel</Button>
        <Button type="button" loading={loading} disabled={!picked} onClick={() => picked && onSubmit(picked.bookingId, picked.bookingRoomId)}>Exchange rooms</Button>
      </div>
    </section>
  );
}

function RatePlanForm({ booking, propertyId, loading, error, onCancel, onSubmit }: {
  booking: Booking;
  propertyId: string;
  loading: boolean;
  error: Error | null;
  onCancel: () => void;
  onSubmit: (input: { ratePlanId: string | null }) => void;
}) {
  // Only plans valid for every night of this stay; on festival dates only the festival plan (no "no plan").
  const roomTypeIds = [...new Set((booking.allottedRooms ?? []).filter((row) => row.status !== "CANCELLED").map((row) => row.room?.roomTypeId).filter(Boolean)
    .concat(booking.roomTypeLines?.map((line) => line.roomTypeId) ?? [], booking.allottedRooms?.length ? [] : booking.roomTypeId ? [booking.roomTypeId] : []))];
  const day = (value: string) => value.slice(0, 10);
  const ratePlans = useQuery({
    queryKey: ["rate-plans", propertyId, "for-stay", roomTypeIds.join(","), day(booking.checkInDate), day(booking.checkOutDate)],
    queryFn: () => api<{ plans: RatePlan[]; required: boolean }>(`/rate-plans/for-stay?propertyId=${propertyId}&roomTypeIds=${roomTypeIds.join(",")}&checkIn=${day(booking.checkInDate)}&checkOut=${day(booking.checkOutDate)}`),
  });
  const options = (ratePlans.data?.plans ?? []).filter((plan) => plan.isActive);
  const required = Boolean(ratePlans.data?.required);
  const [ratePlanId, setRatePlanId] = useState(booking.ratePlanId ?? "");

  return (
    <section className="booking-inline-form">
      <h4>Change rate plan</h4>
      <div className="property-form-grid">
        <label>Rate plan
          <select value={ratePlanId} onChange={(event) => setRatePlanId(event.target.value)}>
            <option value="">{required ? "Choose the festival rate" : "No rate plan"}</option>
            {options.map((plan) => <option key={plan.id} value={plan.id}>{plan.name}{plan.isSeasonal ? " (festival)" : ""}</option>)}
          </select>
        </label>
      </div>
      <div className="form-hint">The booking total is recalculated automatically from the room's tariff and the selected rate plan. Any balance can then be collected with Record payment.</div>
      {error && <div className="form-error">{error.message}</div>}
      <div className="booking-inline-form__actions">
        <Button type="button" variant="ghost" onClick={onCancel}>Cancel</Button>
        <Button type="button" loading={loading} disabled={required && !ratePlanId} onClick={() => onSubmit({ ratePlanId: ratePlanId || null })}>Save rate plan</Button>
      </div>
    </section>
  );
}

function guestCountText(booking: Booking) {
  const adults = booking.adults ?? booking.noOfGuests;
  const children = booking.children ?? 0;
  const adultsText = `${adults} adult${adults === 1 ? "" : "s"}`;
  return children ? `${adultsText}, ${children} child${children === 1 ? "" : "ren"}` : adultsText;
}

// Guests are a count only. Raising it asks the owner for a one-off extra-guest charge (0 is fine).
function GuestCountForm({ adults: initialAdults, children: initialChildren, loading, error, onCancel, onSubmit }: {
  adults: number;
  children: number;
  loading: boolean;
  error: Error | null;
  onCancel: () => void;
  onSubmit: (input: { adults: number; children: number; extraCharge?: number }) => void;
}) {
  const [adults, setAdults] = useState(String(initialAdults));
  const [children, setChildren] = useState(String(initialChildren));
  const [extraCharge, setExtraCharge] = useState("0");
  const adultsCount = Number(adults);
  const childrenCount = Number(children);
  const valid = adults !== "" && children !== "" && Number.isInteger(adultsCount) && adultsCount >= 1 && Number.isInteger(childrenCount) && childrenCount >= 0;
  const added = valid ? adultsCount + childrenCount - (initialAdults + initialChildren) : 0;
  const unchanged = adultsCount === initialAdults && childrenCount === initialChildren;
  const chargeValid = added <= 0 || (extraCharge !== "" && Number(extraCharge) >= 0);

  return (
    <section className="booking-inline-form">
      <h4>Edit guests</h4>
      <div className="property-form-grid">
        <label>Adults<input type="number" min={1} inputMode="numeric" value={adults} onChange={(event) => setAdults(event.target.value)} /></label>
        <label>Children<input type="number" min={0} inputMode="numeric" value={children} onChange={(event) => setChildren(event.target.value)} /></label>
        {added > 0 && (
          <label className="property-form-grid__wide">Extra guest charge (+{added} guest{added > 1 ? "s" : ""})<input type="number" min={0} step="0.01" inputMode="decimal" value={extraCharge} onChange={(event) => setExtraCharge(event.target.value)} /></label>
        )}
      </div>
      {added > 0 && <div className="form-hint">One amount for the whole stay. Enter 0 if there is no extra charge.</div>}
      {error && <div className="form-error">{error.message}</div>}
      <div className="booking-inline-form__actions">
        <Button type="button" variant="ghost" onClick={onCancel}>Cancel</Button>
        <Button type="button" loading={loading} disabled={!valid || unchanged || !chargeValid} onClick={() => onSubmit({ adults: adultsCount, children: childrenCount, extraCharge: added > 0 ? Number(extraCharge) : undefined })}>Save guests</Button>
      </div>
    </section>
  );
}

function AddServiceForm({ propertyId, loading, error, onCancel, onSubmit }: {
  propertyId: string;
  loading: boolean;
  error: Error | null;
  onCancel: () => void;
  onSubmit: (input: { serviceId: string; quantity: number }) => void;
}) {
  const services = useQuery({ queryKey: ["services", propertyId], queryFn: () => api<ListResponse<Service>>(`/services?propertyId=${propertyId}`) });
  const options = listItems<Service>(services.data).filter((service) => service.isAvailable);
  const [serviceId, setServiceId] = useState("");
  const [quantity, setQuantity] = useState("1");

  return (
    <section className="booking-inline-form">
      <h4>Add service</h4>
      <div className="property-form-grid">
        <label>Service
          <select value={serviceId} onChange={(event) => setServiceId(event.target.value)}>
            <option value="">Select a service</option>
            {options.map((service) => <option key={service.id} value={service.id}>{service.title} - {currency.format(Number(service.price))}</option>)}
          </select>
        </label>
        <label>Quantity<input type="number" min={1} value={quantity} onChange={(event) => setQuantity(event.target.value)} /></label>
      </div>
      {error && <div className="form-error">{error.message}</div>}
      <div className="booking-inline-form__actions">
        <Button type="button" variant="ghost" onClick={onCancel}>Cancel</Button>
        <Button type="button" loading={loading} disabled={!serviceId} onClick={() => onSubmit({ serviceId, quantity: Math.max(1, Number(quantity || 1)) })}>Add service</Button>
      </div>
    </section>
  );
}

function AddChargeForm({ loading, error, onCancel, onSubmit }: {
  loading: boolean;
  error: Error | null;
  onCancel: () => void;
  onSubmit: (input: { label: string; amount: number }) => void;
}) {
  const [label, setLabel] = useState("");
  const [amount, setAmount] = useState("");

  return (
    <section className="booking-inline-form">
      <h4>Add extra charge</h4>
      <div className="property-form-grid">
        <label>Reason<input value={label} onChange={(event) => setLabel(event.target.value)} placeholder="e.g. Minibar, Damage fee" /></label>
        <label>Amount<input type="number" min={0} step="0.01" value={amount} onChange={(event) => setAmount(event.target.value)} /></label>
      </div>
      {error && <div className="form-error">{error.message}</div>}
      <div className="booking-inline-form__actions">
        <Button type="button" variant="ghost" onClick={onCancel}>Cancel</Button>
        <Button type="button" loading={loading} disabled={label.trim().length < 2 || !amount} onClick={() => onSubmit({ label: label.trim(), amount: Number(amount) })}>Add charge</Button>
      </div>
    </section>
  );
}

function BookingInfoPanel({ title, icon: Icon, rows }: { title: string; icon: LucideIcon; rows: [string, string][] }) {
  return (
    <div className="booking-info-panel">
      <h4><Icon size={17} /> {title}</h4>
      {rows.map(([label, value]) => <div key={label}><small>{label}</small><strong>{value}</strong></div>)}
    </div>
  );
}

function getBookingStatusOptions(status: Booking["bookingStatus"]): Booking["bookingStatus"][] {
  if (status === "PENDING") return ["CONFIRMED", "REJECTED"];
  // Bookings only reach COMPLETED through the check-out flow (see /bookings/:id/check-out),
  // which records the late-checkout charge — there's no direct "Complete" jump from here.
  if (status === "CONFIRMED") return ["CANCELLED"];
  return [];
}

// Who did what on this booking - loaded when opened, refreshed after every change (version / payments).
function BookingHistory({ bookingId, refreshKey }: { bookingId: string; refreshKey: string }) {
  const [open, setOpen] = useState(false);
  const history = useQuery({
    queryKey: ["booking-history", bookingId, refreshKey],
    queryFn: () => api<ActivityEntry[]>(`/bookings/${bookingId}/history`),
    enabled: open,
  });
  return (
    <section className="booking-line-section booking-history">
      <button type="button" className="booking-line-section__head booking-history__toggle" aria-expanded={open} onClick={() => setOpen((value) => !value)}>
        <h4><History size={16} /> History</h4>
        <ChevronDown size={16} className={open ? "is-open" : undefined} />
      </button>
      {open && history.isLoading && <p className="booking-line-section__empty">Loading...</p>}
      {open && history.error && <div className="form-error">{history.error.message}</div>}
      {open && history.data && (history.data.length ? <ActivityList entries={history.data} /> : <p className="booking-line-section__empty">Nothing recorded yet.</p>)}
    </section>
  );
}

function stayChargeLabel(type?: StayChargeType) {
  if (type === "FULL") return "Full night";
  if (type === "HALF_DAY") return "Half day";
  if (type === "MANUAL") return "Manual";
  return "None";
}

// Second step of every booking change: a summary of what will happen, plus a typed code (cancel / reject /
// no-show) or an acknowledgement tick (refund) for the risky ones.
function ConfirmPanel({ confirm, busy, onCancel }: { confirm: PendingConfirm; busy: boolean; onCancel: () => void }) {
  const [typed, setTyped] = useState("");
  const [acknowledged, setAcknowledged] = useState(false);
  const [reason, setReason] = useState("");
  const ready = (!confirm.typeToConfirm || typed.trim().toUpperCase() === confirm.typeToConfirm) && (!confirm.acknowledge || acknowledged)
    && (!confirm.reasonLabel || reason.trim().length >= 3);
  return (
    <div className="bd-confirm" role="alertdialog" aria-labelledby="bd-confirm-title">
      <p id="bd-confirm-title"><strong>{confirm.title}</strong></p>
      <dl className="bd-confirm__lines">
        {confirm.lines.map(([label, value]) => <div key={label}><dt>{label}</dt><dd>{value}</dd></div>)}
      </dl>
      {confirm.note && <p className="bd-confirm__note">{confirm.note}</p>}
      {confirm.typeToConfirm && (
        <label className="bd-confirm__type">Type <b>{confirm.typeToConfirm}</b> (last 4 characters of the booking ref) to confirm
          <input value={typed} autoComplete="off" autoCapitalize="characters" onChange={(event) => setTyped(event.target.value)} />
        </label>
      )}
      {confirm.reasonLabel && (
        <label className="bd-confirm__type">{confirm.reasonLabel}
          <input value={reason} maxLength={300} onChange={(event) => setReason(event.target.value)} />
        </label>
      )}
      {confirm.acknowledge && <label className="checkbox-row"><input type="checkbox" checked={acknowledged} onChange={(event) => setAcknowledged(event.target.checked)} /> {confirm.acknowledge}</label>}
      <div className="bd-actions__row">
        <Button variant="secondary" disabled={busy} onClick={onCancel}>Go back</Button>
        <Button variant={confirm.danger ? "danger" : "primary"} loading={busy} disabled={!ready} onClick={() => confirm.run(confirm.reasonLabel ? reason.trim() : undefined)}>{confirm.confirmLabel}</Button>
      </div>
    </div>
  );
}

function statusActionLabel(status: Booking["bookingStatus"]) {
  if (status === "CONFIRMED") return "Confirm";
  if (status === "COMPLETED") return "Complete";
  if (status === "CANCELLED") return "Cancel";
  if (status === "REJECTED") return "Reject";
  return status;
}

function formatDateTime(value: string, time?: string) {
  const date = new Intl.DateTimeFormat("en-IN", { day: "numeric", month: "short", year: "numeric", timeZone: PROPERTY_TIME_ZONE }).format(new Date(value));
  return time ? `${date}, ${formatTime(time)}` : `${date}, ${new Intl.DateTimeFormat("en-IN", { hour: "numeric", minute: "2-digit", timeZone: PROPERTY_TIME_ZONE }).format(new Date(value))}`;
}

function formatTime(value: string) {
  const [hours = "0", minutes = "0"] = value.split(":");
  const date = new Date();
  date.setHours(Number(hours), Number(minutes), 0, 0);
  return new Intl.DateTimeFormat("en-IN", { hour: "numeric", minute: "2-digit" }).format(date);
}
